import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useContext,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Table,
  Button,
  Input,
  Space,
  Tag,
  Tooltip,
  Popconfirm,
  Row,
  Col,
  Statistic,
  Dropdown,
  Menu,
  Badge,
  Modal,
  Form,
  Select,
  Typography,
  message,
  Divider,
  Alert,
  Spin,
  Tabs,
  Avatar,
  Empty,
  Switch,
} from "antd";
import {
  PlusOutlined,
  SearchOutlined,
  FileTextOutlined,
  EditOutlined,
  DeleteOutlined,
  SendOutlined,
  MoreOutlined,
  CopyOutlined,
  PhoneOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  ArrowLeftOutlined,
  LinkOutlined,
  MessageOutlined,
  StarOutlined,
  FilterOutlined,
  ClockCircleOutlined,
  BarChartOutlined,
  ThunderboltOutlined,
  ReloadOutlined,
  ClearOutlined,
  LoadingOutlined,
  MinusCircleOutlined,
  SettingOutlined,
  WhatsAppOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import axiosConfig from "../../../../config/axiosConfig";
import { AuthContext } from "../../../../context/authContext";
import WriteGuard from "../../../../components/WriteGuard";
import { useWaba } from "../../../../context/wabaContext";
import AdvancedTemplateBuilder from "./AdvancedTemplateBuilder";
import { Skeleton as BoneSkeleton } from "boneyard-js/react";
import "./TemplateBuilder.css";
import "./AdvancedTemplateBuilder.css";
import "./TemplatesPage.css";
const { Option } = Select;
const { TextArea } = Input;
const { Text, Title } = Typography;

const TemplatesPage = () => {
  const { currentUser } = useContext(AuthContext);
  const navigate = useNavigate();
  
  // WABA Details from Context for validation
  const { hasWabaDetails, loadingWabaDetails, wabaFetchError } = useWaba();

  // Skeletal Loading Components with prefers-reduced-motion support
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const TemplateCardSkeleton = () => (
    <div style={{
      background: '#fff',
      borderRadius: 14,
      padding: 12,
      boxShadow: '0 8px 24px rgba(15,23,42,0.07)',
      border: '1px solid #e6edf8',
      height: '100%',
      minHeight: 320,
      display: 'flex',
      flexDirection: 'column'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, gap: 8 }}>
        <div className="skeleton-shimmer" style={{
          height: 22,
          borderRadius: 999,
          width: 88,
        }} />
        <div className="skeleton-shimmer" style={{
          height: 24,
          width: 96,
          borderRadius: 999,
        }} />
      </div>
      
      <div style={{
        background: '#e5ddd5',
        borderRadius: 12,
        padding: 14,
        marginBottom: 12,
        minHeight: 190,
        backgroundImage: 'radial-gradient(rgba(148,163,184,0.16) 0.8px, transparent 0.8px)',
        backgroundSize: '8px 8px',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'flex-end'
      }}>
        <div style={{
          background: '#dcf8c6',
          borderRadius: '10px 10px 2px 10px',
          padding: 12,
          maxWidth: '88%',
          width: '88%',
          marginLeft: 'auto',
          boxShadow: '0 1px 0.5px rgba(0,0,0,0.13)',
          position: 'relative'
        }}>
          <div style={{
            position: 'absolute',
            right: -6,
            top: 0,
            width: 12,
            height: 12,
            background: 'linear-gradient(225deg, #dcf8c6 50%, transparent 50%)'
          }} />
          <div className="skeleton-shimmer" style={{
            height: 14,
            borderRadius: 4,
            marginBottom: 8,
            width: '100%',
            background: 'linear-gradient(90deg, #c8e6c9 25%, #a5d6a7 50%, #c8e6c9 75%)',
          }} />
          <div className="skeleton-shimmer" style={{
            height: 14,
            borderRadius: 4,
            marginBottom: 8,
            width: '85%',
            background: 'linear-gradient(90deg, #c8e6c9 25%, #a5d6a7 50%, #c8e6c9 75%)',
          }} />
          <div className="skeleton-shimmer" style={{
            height: 14,
            borderRadius: 4,
            marginBottom: 8,
            width: '60%',
            background: 'linear-gradient(90deg, #c8e6c9 25%, #a5d6a7 50%, #c8e6c9 75%)',
          }} />
          <div style={{
            borderTop: '1px solid rgba(0,0,0,0.08)',
            marginTop: 10,
            paddingTop: 10,
          }}>
            <div className="skeleton-shimmer" style={{
              height: 14,
              borderRadius: 6,
              width: '70%',
              margin: '0 auto 8px',
              background: 'linear-gradient(90deg, #c8e6c9 25%, #a5d6a7 50%, #c8e6c9 75%)',
            }} />
            <div className="skeleton-shimmer" style={{
              height: 14,
              borderRadius: 6,
              width: '60%',
              margin: '0 auto',
              background: 'linear-gradient(90deg, #c8e6c9 25%, #a5d6a7 50%, #c8e6c9 75%)',
            }} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
            <div className="skeleton-shimmer" style={{
              height: 12,
              borderRadius: 4,
              width: 50,
              background: 'linear-gradient(90deg, #c8e6c9 25%, #a5d6a7 50%, #c8e6c9 75%)',
            }} />
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 12 }}>
        <div style={{ flex: 1 }}>
          <div className="skeleton-shimmer" style={{
            height: 16,
            borderRadius: 4,
            marginBottom: 6,
            width: '58%',
          }} />
          <div className="skeleton-shimmer" style={{
            height: 12,
            borderRadius: 4,
            width: '42%',
          }} />
        </div>
      </div>
      
      <div style={{ display: 'flex', gap: 8, marginTop: 'auto' }}>
        <div className="skeleton-shimmer" style={{
          height: 36,
          borderRadius: 10,
          flex: 1,
        }} />
        <div className="skeleton-shimmer" style={{
          height: 36,
          width: 36,
          borderRadius: 10,
        }} />
        <div className="skeleton-shimmer" style={{
          height: 36,
          width: 36,
          borderRadius: 10,
        }} />
      </div>
    </div>
  );

  const MyTemplatesGridSkeleton = () => (
    <div>
      {/* Search Bar Skeleton */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 20,
        flexWrap: 'wrap',
        gap: 16
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div className="skeleton-shimmer" style={{
            height: 40,
            borderRadius: 8,
            width: 280,
          }} />
          <div className="skeleton-shimmer" style={{
            height: 40,
            borderRadius: 8,
            width: 100,
          }} />
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 10,
            padding: '8px 14px',
            background: '#fafafa',
            borderRadius: 8,
            border: '1px solid #f0f0f0'
          }}>
            {[80, 90, 80, 80].map((width, i) => (
              <div key={i} className="skeleton-shimmer" style={{
                height: 28,
                borderRadius: 4,
                width,
              }} />
            ))}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <div className="skeleton-shimmer" style={{
            height: 40,
            borderRadius: 8,
            width: 100,
          }} />
          <div className="skeleton-shimmer" style={{
            height: 40,
            borderRadius: 8,
            width: 150,
          }} />
        </div>
      </div>
      
      {/* Template Cards Grid */}
      <Row gutter={[20, 20]}>
        {Array.from({ length: 6 }).map((_, index) => (
          <Col xs={24} sm={12} lg={8} key={index}>
            <TemplateCardSkeleton />
          </Col>
        ))}
      </Row>
    </div>
  );

  const LibraryGridSkeleton = () => (
    <Row gutter={[16, 16]}>
      {Array.from({ length: 6 }).map((_, index) => (
        <Col xs={24} sm={12} lg={8} key={index}>
          <TemplateCardSkeleton />
        </Col>
      ))}
    </Row>
  );

  // Global error handler for unhandled promise rejections
  useEffect(() => {
    const handleUnhandledRejection = (event) => {
      console.error("Unhandled promise rejection:", event.reason);

      // Check if it's a Kafka error and handle it gracefully
      if (
        event.reason &&
        event.reason.message &&
        event.reason.message.includes("Kafka")
      ) {
        event.preventDefault(); // Prevent the error from showing as runtime error
        message.warning(
          "Notification service is temporarily unavailable, but your request was processed."
        );
      }
    };

    window.addEventListener("unhandledrejection", handleUnhandledRejection);

    return () => {
      window.removeEventListener(
        "unhandledrejection",
        handleUnhandledRejection
      );
    };
  }, []);

  const [searchText, setSearchText] = useState("");
  const [createTemplateVisible, setCreateTemplateVisible] = useState(false);
  const [editTemplateVisible, setEditTemplateVisible] = useState(false);
  const [previewTemplateVisible, setPreviewTemplateVisible] = useState(false);
  const [mobilePreviewVisible, setMobilePreviewVisible] = useState(false);
  const [showAdvancedBuilder, setShowAdvancedBuilder] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [advancedFilterVisible, setAdvancedFilterVisible] = useState(false);

  // Template Library state variables
  const [libraryTemplates, setLibraryTemplates] = useState([]);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [librarySearch, setLibrarySearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [selectedIndustries, setSelectedIndustries] = useState([]);
  const [libraryPagination, setLibraryPagination] = useState({
    hasNext: false,
    nextCursor: null,
    total: 0
  });
  // Library category tab: 'all' | 'AUTHENTICATION' | 'UTILITY'
  const [libraryActiveCategory, setLibraryActiveCategory] = useState('all');
  const [libraryTopic, setLibraryTopic] = useState('');
  const [libraryPage, setLibraryPage] = useState(1);
  const LIBRARY_PAGE_SIZE = 20;
  const [templatePreviewVisible, setTemplatePreviewVisible] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const searchTimeoutRef = useRef(null);
  
  // Rate limiting for refresh button
  const [refreshCooldown, setRefreshCooldown] = useState(false);
  const lastRefreshRef = useRef(0);
  const refreshInProgressRef = useRef(false);
  const REFRESH_COOLDOWN_MS = 1000; // 1 second cooldown

  // (Polling removed — user will refresh manually to check status changes)

  // Adopt Template modal state variables
  const [useLibraryTemplateVisible, setUseLibraryTemplateVisible] = useState(false);
  const [selectedLibraryTemplate, setSelectedLibraryTemplate] = useState(null);
  const [useLibraryTemplateLoading, setUseLibraryTemplateLoading] = useState(false);

  // Category data
  const categoryData = {
    UTILITY: {
      count: 133,
      topics: [
        'Account Updates',
        'Call Permissions', 
        'Customer Feedback',
        'Event Reminder',
        'Group Invite Link',
        'Order Management',
        'Payments'
      ],
      industries: ['E-Commerce', 'Financial Services', 'Telecommunication']
    },
    AUTHENTICATION: {
      count: 16,
      topics: ['Identity Verification', 'Payments'],
      industries: ['E-Commerce', 'Financial Services']
    }
  };

  // New state for tabs and pre-approved templates
  // DISABLED: Pre-approved templates functionality
  const [activeTab, setActiveTab] = useState("mytemplates"); // Changed from "preapproved"
  /* DISABLED: Pre-approved templates
  const [preApprovedTemplates] = useState([
    {
      id: "pa_1",
      name: "Welcome Message",
      description: "Warm greeting for new customers",
      content: "Hello {{1}}, Welcome to {{2}}! We're excited to have you on board. 🎉",
      category: "greeting",
      tags: ["welcome", "greeting", "onboarding"],
      usageCount: 2450,
      rating: 4.8,
      color: "#52c41a",
      icon: "👋",
      component: [
        {
          type: "BODY",
          text: "Hello {{1}}, Welcome to {{2}}! We're excited to have you on board. 🎉"
        }
      ]
    },
    {
      id: "pa_2", 
      name: "Order Confirmation",
      description: "Confirm order details with tracking",
      content: "Hi {{1}}, Your order #{{2}} for ${{3}} has been confirmed! Track it here: {{4}}",
      category: "transactional",
      tags: ["order", "confirmation", "ecommerce"],
      usageCount: 1890,
      rating: 4.9,
      color: "#1890ff",
      icon: "📦",
      component: [
        {
          type: "BODY",
          text: "Hi {{1}}, Your order #{{2}} for ${{3}} has been confirmed!"
        },
        {
          type: "BUTTONS",
          buttons: [
            {
              type: "URL",
              text: "Track Order",
              url: "https://example.com/track/{{2}}"
            }
          ]
        }
      ]
    },
    {
      id: "pa_3",
      name: "Appointment Reminder", 
      description: "Gentle reminder for upcoming appointments",
      content: "Hi {{1}}, This is a reminder for your appointment on {{2}} at {{3}}. See you soon! 📅",
      category: "utility",
      tags: ["appointment", "reminder", "healthcare"],
      usageCount: 3200,
      rating: 4.7,
      color: "#722ed1",
      icon: "📅",
      component: [
        {
          type: "BODY", 
          text: "Hi {{1}}, This is a reminder for your appointment on {{2}} at {{3}}. See you soon! 📅"
        }
      ]
    },
    {
      id: "pa_4",
      name: "Payment Reminder",
      description: "Friendly payment due notification",
      content: "Hello {{1}}, Your payment of ${{2}} is due on {{3}}. Please complete it to avoid any interruption.",
      category: "utility",
      tags: ["payment", "reminder", "billing"],
      usageCount: 1650,
      rating: 4.6,
      color: "#fa8c16",
      icon: "💳",
      component: [
        {
          type: "BODY",
          text: "Hello {{1}}, Your payment of ${{2}} is due on {{3}}. Please complete it to avoid any interruption."
        }
      ]
    },
    {
      id: "pa_5",
      name: "Shipping Update",
      description: "Order shipped notification with tracking",
      content: "Great news {{1}}! Your order #{{2}} has been shipped and will arrive by {{3}}. 🚚",
      category: "transactional", 
      tags: ["shipping", "delivery", "ecommerce"],
      usageCount: 2100,
      rating: 4.8,
      color: "#13c2c2",
      icon: "🚚",
      component: [
        {
          type: "BODY",
          text: "Great news {{1}}! Your order #{{2}} has been shipped and will arrive by {{3}}. 🚚"
        }
      ]
    },
    {
      id: "pa_6",
      name: "Discount Offer",
      description: "Limited time promotional offer",
      content: "🎉 Hey {{1}}! Get {{2}}% OFF your next purchase. Use code {{3}} before {{4}}. Shop now!",
      category: "promotional",
      tags: ["discount", "promotion", "limited-time"],
      usageCount: 4100,
      rating: 4.9,
      color: "#eb2f96",
      icon: "🎉",
      component: [
        {
          type: "BODY", 
          text: "🎉 Hey {{1}}! Get {{2}}% OFF your next purchase. Use code {{3}} before {{4}}. Shop now!"
        }
      ]
    },
    {
      id: "pa_7",
      name: "Support Follow-up",
      description: "Check customer satisfaction after support",
      content: "Hi {{1}}, How was your experience with our support team? We'd love to hear your feedback! 😊",
      category: "support",
      tags: ["support", "feedback", "follow-up"],
      usageCount: 890,
      rating: 4.5,
      color: "#f759ab",
      icon: "💬",
      component: [
        {
          type: "BODY",
          text: "Hi {{1}}, How was your experience with our support team? We'd love to hear your feedback! 😊"
        }
      ]
    },
    {
      id: "pa_8",
      name: "Event Invitation",
      description: "Invite customers to special events",
      content: "You're invited! Join us for {{1}} on {{2}} at {{3}}. RSVP by replying YES. 🎊",
      category: "promotional",
      tags: ["event", "invitation", "engagement"], 
      usageCount: 670,
      rating: 4.4,
      color: "#52c41a",
      icon: "🎊",
      component: [
        {
          type: "BODY",
          text: "You're invited! Join us for {{1}} on {{2}} at {{3}}. RSVP by replying YES. 🎊"
        },
        {
          type: "BUTTONS",
          buttons: [
            {
              type: "QUICK_REPLY",
              text: "RSVP YES"
            },
            {
              type: "QUICK_REPLY", 
              text: "Can't Make It"
            }
          ]
        }
      ]
    }
  ]);
  */

  // API state
  const [templates, setTemplates] = useState([]);
  const [filteredTemplates, setFilteredTemplates] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 12,
    total: 0,
  });

  // Advanced filters
  const [activeFilters, setActiveFilters] = useState({
    search: "",
    templatename: "",
    status: "",
    language: "",
    templatecategory: "",
  });

  // Form instances
  const [createForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const [filterForm] = Form.useForm();
  const [adoptForm] = Form.useForm();

  // Default templates state (for ADMIN users)
  const [defaultTemplates, setDefaultTemplates] = useState([]);
  const [defaultTemplateLoading, setDefaultTemplateLoading] = useState(false);
  const [settingDefaultTemplate, setSettingDefaultTemplate] = useState({});
  
  // Local state to track desired defaults (as backup approach)
  const [localDefaultTemplates, setLocalDefaultTemplates] = useState(new Set());
  
  // Check if current user is ADMIN (only ADMIN can set default templates)
  // Get company_type from session storage directly
  const getCompanyTypeFromStorage = () => {
    try {
      // Check different possible storage keys and formats
      let companyType = sessionStorage.getItem('company_type') || 
                       localStorage.getItem('company_type');
      
      // If not found, try to get from user data stored in sessionStorage
      if (!companyType) {
        const userData = sessionStorage.getItem('user') || sessionStorage.getItem('currentUser');
        if (userData) {
          try {
            const parsedUser = JSON.parse(userData);
            companyType = parsedUser.company_type;
          } catch (e) {
            console.log('Could not parse user data from storage');
          }
        }
      }
      
      // Also check if it's stored in the token payload or company object
      if (!companyType) {
        const company = sessionStorage.getItem('company');
        if (company) {
          try {
            const parsedCompany = JSON.parse(company);
            companyType = parsedCompany.company_type;
          } catch (e) {
            console.log('Could not parse company data from storage');
          }
        }
      }
      
      console.log('Company type from storage:', companyType);
      return companyType;
    } catch (error) {
      console.error('Error reading company_type from storage:', error);
      return null;
    }
  };
  
  const companyType = getCompanyTypeFromStorage();
  const isAdmin = companyType === 'ADMIN';
  
  // All user types can see/use default templates, but only ADMIN can set them
  const canSetDefaults = isAdmin;
  const canViewDefaults = true; // All users can view/use default templates
  
  // Debug logging for user roles and storage
  console.log('=== STORAGE DEBUG ===');
  console.log('All sessionStorage keys:', Object.keys(sessionStorage));
  console.log('SessionStorage contents:');
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i);
    const value = sessionStorage.getItem(key);
    console.log(`${key}:`, value);
  }
  console.log('User Role Debug:', {
    currentUser_company_type: currentUser?.company_type,
    currentUser: currentUser,
    storage_company_type: companyType,
    isAdmin,
    canSetDefaults,
    canViewDefaults
  });
  console.log('====================');
  

  // Status mapping for API
  const statusMapping = {
    'Pending': 0,
    'Approved': 1,
    'Rejected': 2,
    'Discarded': 3,
    // Also support lowercase for backward compatibility
    'pending': 0,
    'approved': 1,
    'rejected': 2,
    'discarded': 3,
  };

  // Fetch templates from API
  const fetchTemplates = async (
    filters = activeFilters,
    page = pagination.current,
    size = pagination.pageSize
  ) => {
    setTableLoading(true);
    try {
      // Map status string to numeric value if status filter is provided
      const mappedFilters = { ...filters };
      if (filters.status && typeof filters.status === 'string') {
        if (statusMapping.hasOwnProperty(filters.status)) {
          mappedFilters.status = statusMapping[filters.status];
        }
      }

      const requestBody = {
        page,
        pageSize: size,
        ...mappedFilters,
      };

      const response = await axiosConfig.post("/template/details", requestBody);

      console.log('API Response:', {
        request: requestBody,
        response: response.data
      });

      if (response.data && response.data.statuscode === 200) {
        // Map API response to our expected format
        const apiTemplates = response.data.entity?.templates || [];

        const mappedTemplates = apiTemplates.map((template, index) => {
          const { body: parsedBody, footer: parsedFooter } = extractFooterAndBody(template);
          return {
            id: template.templateid,
            rowno: template.rowno,
            name: template.templatename,
            description: `${template.templatelanguage?.toUpperCase()} template`,
            content: template.templatecontent,
            body: parsedBody,
            footer: parsedFooter,
            variables: extractVariables(template.templatecontent),
            status: template.status.toLowerCase(),
            usageCount: Math.floor(Math.random() * 300),
            lastUsed: index === 0 ? "1 hour ago" : index === 1 ? "2 days ago" : "1 week ago",
            category: template.templatecategory || "utility",
            createdAt: new Date().toISOString().split("T")[0],
            language: template.templatelanguage,
            originalStatus: template.status.toUpperCase(),
            // Track if this template came from the library
            source: template.source || null,
            // Include defaultemplate field from API
            defaultemplate: template.defaultemplate || false,
            // Include component structure from API response (already an array)
            component: template.component || [],
            // Include media header fields from API
            header_format: template.header_format || null,
            file_path: template.file_path || null,
            header_media_url: template.header_media_url || null,
            // Extract buttons for easier access
            buttons: (() => {
              const buttonsComponent = template.component?.find(c => c.type === 'BUTTONS');
              return buttonsComponent?.buttons || [];
            })(),
          };
        });

        // Update templates and filteredTemplates
        setTemplates(mappedTemplates);
        setFilteredTemplates(mappedTemplates);
        
        // Update pagination
        setPagination({
          current: response.data.entity?.page || 1,
          pageSize: response.data.entity?.pageSize || size,
          total: response.data.entity?.total || 0,
        });

        // If no templates found and we have a status filter, show a message
        // if (mappedTemplates.length === 0 && filters.status) {
        //   message.info(`No templates found with status: ${filters.status}`);
        // }
      } else if (response.data && response.data.statuscode === 404) {
        // Handle 404 - No templates found
        console.log('No templates found for the given filters');
        setTemplates([]);
        setFilteredTemplates([]);
        setPagination({
          current: 1,
          pageSize: size,
          total: 0,
        });
        
        // Show appropriate message based on filters
        if (filters.status) {
          const statusName = Object.keys(statusMapping).find(
            key => statusMapping[key] === mappedFilters.status
          ) || filters.status;
          // message.info(`No templates found with status: ${statusName}`);
        } else {
          // message.info('No templates found');
        }
      } else {
        // Handle other error cases
        console.error('API Error:', response.data);
        setTemplates([]);
        setFilteredTemplates([]);
        setPagination({
          current: 1,
          pageSize: size,
          total: 0,
        });
      }
    } catch (error) {
      console.error("Error fetching templates:", error);
      // message.error("Failed to fetch templates");

      // Fallback to mock data if API fails
      // const mockTemplates = [
      //   {
      //     id: 1,
      //     name: "Welcome Message",
      //     description: "Initial greeting for new users",
      //     content:
      //       "Hello {{name}}, Welcome to MessageOS! We're excited to have you on board.",
      //     variables: ["name"],
      //     status: "active",
      //     usageCount: 245,
      //     lastUsed: "2 days ago",
      //     category: "greeting",
      //     createdAt: "2025-01-01",
      //   },
      //   {
      //     id: 2,
      //     name: "Order Confirmation",
      //     description: "Confirm order details",
      //     content:
      //       "Hello {{name}}, Your order #{{order_id}} has been confirmed and will be shipped on {{ship_date}}.",
      //     variables: ["name", "order_id", "ship_date"],
      //     status: "active",
      //     usageCount: 189,
      //     lastUsed: "1 day ago",
      //     category: "transactional",
      //     createdAt: "2025-01-02",
      //   },
      //   {
      //     id: 3,
      //     name: "Special Offer",
      //     description: "Promotional message with discount",
      //     content:
      //       "Hello {{name}}, Get {{discount}}% off until {{end_date}}! Use code {{promo_code}} at checkout.",
      //     variables: ["name", "discount", "end_date", "promo_code"],
      //     status: "inactive",
      //     usageCount: 67,
      //     lastUsed: "1 week ago",
      //     category: "promotional",
      //     createdAt: "2025-01-03",
      //   },
      //   {
      //     id: 4,
      //     name: "Support Response",
      //     description: "Customer support template",
      //     content:
      //       "Hi {{name}}, Thank you for contacting us. We have received your query and will respond within 24 hours.",
      //     variables: ["name"],
      //     status: "active",
      //     usageCount: 156,
      //     lastUsed: "5 hours ago",
      //     category: "support",
      //     createdAt: "2025-01-04",
      //   },
      // ];
      // setTemplates(mockTemplates);
      // setFilteredTemplates(mockTemplates);
      // setPagination({ current: 1, pageSize: 10, total: mockTemplates.length });
    } finally {
      setTableLoading(false);
    }
  };

  // Fetch default templates from API using GET /template/default (Pre-approved Templates API)
  const fetchDefaultTemplates = async () => {
    // All users can see default templates in pre-approved section
    
    setDefaultTemplateLoading(true);
    try {
      // Use the specific GET /template/default API endpoint for pre-approved templates
      const response = await axiosConfig.get("/template/default");
      
      if (response.data && response.data.statuscode === 200) {
        // Handle the actual API response structure - note: it's 'templates' not 'template'
        const templatesData = response.data.entity?.templates;
        
        console.log('Default templates API response:', {
          statuscode: response.data.statuscode,
          templatesCount: templatesData?.length || 0,
          templatesData: templatesData
        });
        
        if (templatesData && Array.isArray(templatesData)) {
          // Map the API response to the expected frontend format
          const mappedDefaultTemplates = templatesData.map((template) => {
            const { body: parsedBody, footer: parsedFooter } = extractFooterAndBody(template);
            return {
            id: template.templateid,
            name: template.templatename,
            description: `${template.templatelanguage?.toUpperCase()} template - ${template.templatecategory}`,
            content: template.templatecontent,
            body: parsedBody,
            footer: parsedFooter,
            variables: extractVariables(template.templatecontent),
            status: template.status.toLowerCase(),
            category: template.templatecategory || "utility",
            language: template.templatelanguage,
            createdAt: template.created_at,
            defaultemplate: template.defaultemplate || true,
            component: template.component || [],
            // Extract buttons for easier access
            buttons: (() => {
              const buttonsComponent = template.component?.find(c => c.type === 'BUTTONS');
              return buttonsComponent?.buttons || [];
            })(),
            // Add some UI-friendly properties
            usageCount: Math.floor(Math.random() * 1000) + 500, // Mock usage count for display
            rating: 4.5 + Math.random() * 0.5, // Mock rating between 4.5-5.0
            color: getTemplateColor(template.templatecategory),
            icon: getTemplateIcon(template.templatecategory),
            // Add properties needed for pre-approved templates display
            tags: [template.templatecategory, 'default', 'approved'],
            isApiDefault: true, // Flag to identify API defaults
            originalTemplate: template // Keep original template for preview
          };
          });
          
          console.log('Mapped default templates:', mappedDefaultTemplates);
          setDefaultTemplates(mappedDefaultTemplates);
        } else {
          setDefaultTemplates([]);
        }
      } else {
        setDefaultTemplates([]);
      }
    } catch (error) {
      console.error("Error fetching default templates:", error);
      // Don't show error to user as this is not critical functionality
      setDefaultTemplates([]);
    } finally {
      setDefaultTemplateLoading(false);
    }
  };

  // Set/unset template as default (supports multiple defaults)
  const handleSetDefaultTemplate = async (templateId, setAsDefault = true) => {
    if (!canSetDefaults) {
      message.error("Only administrators can set default templates");
      return;
    }
    
    setSettingDefaultTemplate(prev => ({ ...prev, [templateId]: true }));
    
    try {
      const requestBody = {
        templateid: templateId,
        setasdefaulttemplate: setAsDefault
      };

      const response = await axiosConfig.post("/template/set-default", requestBody);
      
      if (response.data && response.data.status) {
        message.success(
          setAsDefault 
            ? "Template set as default successfully" 
            : "Template removed from defaults successfully"
        );
        
        // Refresh both templates lists to get updated defaultemplate field
        await fetchTemplates(activeFilters, pagination.current, pagination.pageSize);
        await fetchDefaultTemplates(); // Refresh default templates for pre-approved section
      } else {
        throw new Error(response.data?.message || "Failed to update default template");
      }
    } catch (error) {
      console.error("Error setting default template:", error);
      const errorMessage = error.response?.data?.message || error.message || "Failed to update default template";
      message.error(errorMessage);
    } finally {
      setSettingDefaultTemplate(prev => ({ ...prev, [templateId]: false }));
    }
  };

  // Sync local default state with API data
  useEffect(() => {
    const currentDefaults = templates.filter(t => t.defaultemplate === true);
    const defaultIds = new Set(currentDefaults.map(t => t.id));
    setLocalDefaultTemplates(defaultIds);
  }, [templates]);

  // Load templates on component mount
  useEffect(() => {
    fetchTemplates();
    fetchDefaultTemplates(); // Fetch default templates for pre-approved section
  }, []);

  // Refresh handler to reload templates with rate limiting
  const handleRefresh = useCallback(async () => {
    const now = Date.now();
    
    // Prevent duplicate calls if already in progress
    if (refreshInProgressRef.current) {
      return;
    }
    
    // Check cooldown period (3 seconds)
    if (now - lastRefreshRef.current < REFRESH_COOLDOWN_MS) {
      message.warning('Please wait a moment before refreshing again');
      return;
    }
    
    // Mark refresh as in progress
    refreshInProgressRef.current = true;
    lastRefreshRef.current = now;
    setRefreshCooldown(true);
    
    // Clear existing data to trigger skeletal loading
    setTemplates([]);
    setFilteredTemplates([]);
    setDefaultTemplates([]);
    
    // Reset pagination
    setPagination({
      current: 1,
      pageSize: 10,
      total: 0,
    });
    
    // Set loading states
    setLoading(true);
    setTableLoading(true);
    setDefaultTemplateLoading(true);
    
    try {
      // Fetch fresh data
      await fetchTemplates(
        activeFilters,
        1, // Reset to first page
        pagination.pageSize
      );
      await fetchDefaultTemplates(); // Also refresh default templates
    } finally {
      refreshInProgressRef.current = false;
      
      // Start cooldown timer
      setTimeout(() => {
        setRefreshCooldown(false);
      }, REFRESH_COOLDOWN_MS);
    }
  }, [activeFilters, pagination.pageSize]);

  // Apply local filters (for client-side filtering when needed)
  const applyLocalFilters = (
    templatesList = templates,
    search = searchText
  ) => {
    let filtered = templatesList;

    if (search) {
      filtered = filtered.filter(
        (template) =>
          template.name.toLowerCase().includes(search.toLowerCase()) ||
          template.description.toLowerCase().includes(search.toLowerCase()) ||
          template.content.toLowerCase().includes(search.toLowerCase())
      );
    }

    setFilteredTemplates(filtered);
    // console.log(filtered);
  };

  // Debounced search function
  const debouncedSearch = useCallback(
    async (searchValue) => {
      setSearchLoading(true);
      const newFilters = { ...activeFilters, search: searchValue };
      setActiveFilters(newFilters);
      await fetchTemplates(newFilters, 1, pagination.pageSize);
      setSearchLoading(false);
    },
    [activeFilters, pagination.pageSize]
  );

  // Handle search
  const handleSearch = (value) => {
    setSearchText(value);

    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Reset search loading if input is cleared
    if (!value.trim()) {
      setSearchLoading(false);
      const newFilters = { ...activeFilters, search: "" };
      setActiveFilters(newFilters);
      fetchTemplates(newFilters, 1, pagination.pageSize);
      return;
    }

    // Show loading state for visual feedback
    setSearchLoading(true);

    // Set new timeout for debounced search (800ms to prevent too frequent searches)
    searchTimeoutRef.current = setTimeout(() => {
      debouncedSearch(value);
    }, 800);
  };

  // Cleanup timeout on component unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Fetch library templates when library tab is active or category/search changes
  useEffect(() => {
    if (activeTab === 'library') {
      setLibraryTopic('');
      setLibraryPage(1);
      fetchLibraryTemplates(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, libraryActiveCategory]);

  // Handle advanced filter
  const handleAdvancedFilter = () => {
    setAdvancedFilterVisible(true);
    filterForm.setFieldsValue(activeFilters);
  };

  // Handle advanced filter form submission
  const handleAdvancedFilterSubmit = async (values) => {
    const newFilters = { ...activeFilters, ...values };
    setActiveFilters(newFilters);
    setAdvancedFilterVisible(false);
    await fetchTemplates(newFilters, 1, pagination.pageSize);
  };

  // Clear all filters
  const handleClearFilters = async () => {
    const clearedFilters = {
      search: "",
      templatename: "",
      status: "",
      language: "",
      templatecategory: "",
    };
    setActiveFilters(clearedFilters);
    setSearchText("");
    filterForm.resetFields();
    await fetchTemplates(clearedFilters, 1, pagination.pageSize);
  };

  // Template Library API Functions
  const fetchLibraryTemplates = async (page = 1, topicOverride) => {
    setLibraryLoading(true);
    try {
      const params = new URLSearchParams();

      // Category filter (skip 'all')
      if (libraryActiveCategory && libraryActiveCategory !== 'all') {
        params.append('category', libraryActiveCategory);
      }

      // Topic filter — prefer explicit override (used when state hasn't updated yet)
      const effectiveTopic = topicOverride !== undefined ? topicOverride : libraryTopic;
      if (effectiveTopic) {
        params.append('topic', effectiveTopic.toUpperCase().replace(/\s+/g, '_'));
      }

      // Search filter
      if (librarySearch.trim()) {
        params.append('search', librarySearch.trim());
      }

      // Pagination
      params.append('page', page);
      params.append('pageSize', LIBRARY_PAGE_SIZE);

      const url = `/template/library?${params.toString()}`;
      const response = await axiosConfig.get(url);

      if (response.data && response.data.statuscode === 200) {
        const entity = response.data.entity;
        const fetchedTemplates = entity?.templates || (Array.isArray(entity) ? entity : []);
        const total = entity?.total ?? fetchedTemplates.length;

        setLibraryTemplates(fetchedTemplates);
        setLibraryPagination({ total, page, pageSize: LIBRARY_PAGE_SIZE });
        setLibraryPage(page);

        // Auto-sync on first open when library is empty
        if (total === 0 && page === 1 && !librarySearch.trim()) {
          try {
            await axiosConfig.post('/template/library/sync');
            const syncResponse = await axiosConfig.get(url);
            if (syncResponse.data?.statuscode === 200) {
              const syncEntity = syncResponse.data.entity;
              const syncTemplates = syncEntity?.templates || (Array.isArray(syncEntity) ? syncEntity : []);
              setLibraryTemplates(syncTemplates);
              setLibraryPagination({ total: syncEntity?.total ?? syncTemplates.length, page, pageSize: LIBRARY_PAGE_SIZE });
            }
          } catch (syncErr) {
            console.warn('Auto-sync failed:', syncErr);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching library templates:', error);
      message.error('Failed to fetch template library');
    } finally {
      setLibraryLoading(false);
    }
  };

  // Debounced search for Template Library (300 ms)
  const handleLibrarySearch = useCallback((value) => {
    setLibrarySearch(value);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setLibraryPage(1);
      fetchLibraryTemplates(1);
    }, 300);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [libraryActiveCategory]);

  // Handle category selection
  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    setSelectedTopics([]);
    setSelectedIndustries([]);
    setLibraryTemplates([]);
    setLibraryPagination({ hasNext: false, nextCursor: null, total: 0 });
  };

  // Handle filter changes
  const handleLibraryFiltersChange = () => {
    fetchLibraryTemplates(1);
  };

  // Clear all library filters
  const handleClearLibraryFilters = () => {
    setLibrarySearch('');
    setSelectedTopics([]);
    setSelectedIndustries([]);
    if (selectedCategory) {
      fetchLibraryTemplates(1);
    }
  };

  // Copy template ID to clipboard
  const handleCopyTemplateId = (templateId) => {
    navigator.clipboard.writeText(templateId).then(() => {
      message.success('Template ID copied to clipboard');
    }).catch(() => {
      message.error('Failed to copy template ID');
    });
  };

  // Handle template preview
  const handleTemplatePreview = (template) => {
    setPreviewTemplate(template);
    setTemplatePreviewVisible(true);
  };

  // Template name validation function (for Ant Design Form validation)
  const validateTemplateName = (_, value) => {
    if (!value || !value.trim()) {
      return Promise.reject(new Error('Template name is required'));
    }
    
    const templateNameRegex = /^[a-z0-9_]+$/;
    if (!templateNameRegex.test(value)) {
      return Promise.reject(new Error('Template name must contain only lowercase letters, numbers, and underscores (no spaces or uppercase letters)'));
    }
    
    if (value.length < 3) {
      return Promise.reject(new Error('Template name must be at least 3 characters long'));
    }
    
    if (value.length > 50) {
      return Promise.reject(new Error('Template name must be less than 50 characters'));
    }
    
    return Promise.resolve();
  };

  // Template name validation function (for JSX usage - returns string or null)
  const getTemplateNameError = (value) => {
    if (!value || !value.trim()) {
      return 'Template name is required';
    }
    
    const templateNameRegex = /^[a-z0-9_]+$/;
    if (!templateNameRegex.test(value)) {
      return 'Template name must contain only lowercase letters, numbers, and underscores (no spaces or uppercase letters)';
    }
    
    if (value.length < 3) {
      return 'Template name must be at least 3 characters long';
    }
    
    if (value.length > 50) {
      return 'Template name must be less than 50 characters';
    }
    
    return null;
  };

  // Open Adopt dialog for a library template
  const handleUseLibraryTemplate = (template) => {
    setSelectedLibraryTemplate(template);
    setUseLibraryTemplateVisible(true);
    // Pre-fill name: convert library name to valid meta format (lowercase + underscores)
    const prefilledName = (template.name || '')
      .toLowerCase()
      .replace(/[^a-z0-9_]/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_|_$/g, '')
      .substring(0, 512);
    adoptForm.setFieldsValue({ customName: prefilledName, language: 'en_US' });
  };

  // Adopt a library template via POST /template/library/adopt
  const handleAdoptTemplate = async () => {
    try {
      const values = await adoptForm.validateFields();
      setUseLibraryTemplateLoading(true);

      const adoptPayload = {
        customName: values.customName,
        language: values.language || 'en_US',
      };
      // Use library_template_id as returned by the browse API
      const libId = selectedLibraryTemplate?.library_template_id || selectedLibraryTemplate?.id;
      if (libId) adoptPayload.libraryTemplateId = libId;
      const response = await axiosConfig.post('/template/library/adopt', adoptPayload);

      if (response.data?.statuscode === 200) {
        message.success('Template adopted successfully — Status: Pending');
        setUseLibraryTemplateVisible(false);
        setSelectedLibraryTemplate(null);
        adoptForm.resetFields();
        // Switch to My Templates and refresh list
        setActiveTab('mytemplates');
        fetchTemplates();
      } else {
        throw new Error(response.data?.message || 'Failed to adopt template');
      }
    } catch (error) {
      if (error.errorFields) return; // Ant Design form validation
      const status = error.response?.status;
      const apiMsg = error.response?.data?.message || '';
      if (status === 400) {
        if (apiMsg.toLowerCase().includes('sync') || apiMsg.toLowerCase().includes('not found')) {
          message.error('Library not synced yet. Please refresh the library first.');
        } else {
          message.error(apiMsg || 'Name must be lowercase letters, digits, or underscores only');
        }
      } else if (status === 500) {
        const metaMsg = error.response?.data?.metaError?.message
          || error.response?.data?.metaApiError?.message
          || apiMsg;
        message.error(metaMsg || 'Meta API error. Please try again.');
      } else {
        message.error(error.message || 'Failed to adopt template');
      }
    } finally {
      setUseLibraryTemplateLoading(false);
    }
  };

  // Handle modal cancel for Adopt dialog
  const handleUseLibraryTemplateCancel = () => {
    setUseLibraryTemplateVisible(false);
    setSelectedLibraryTemplate(null);
    adoptForm.resetFields();
  };

  // Handle pagination change
  const handleTableChange = (paginationConfig) => {
    fetchTemplates(
      activeFilters,
      paginationConfig.current,
      paginationConfig.pageSize
    );
  };

  const columns = [
    {
      title: "#",
      key: "rowno",
      width: 80,
      render: (_, record, index) => {
        // console.log(record);

        return <span style={{ fontWeight: 500 }}>{record.rowno}</span>;
      },
    },
    {
      title: "Template",
      key: "template",
      render: (_, record) => (
        <div>
          <div
            style={{ display: "flex", alignItems: "center", marginBottom: 4 }}
          >
            <FileTextOutlined className="template-theme-icon" style={{ marginRight: 8, color: "#10b981" }} />
            <Text
              strong
              style={{ cursor: "pointer" }}
              onClick={() => {
                setSelectedTemplate(record);
                setMobilePreviewVisible(true);
              }}
            >
              {record.name}
            </Text>
            {/* {record.usageCount > 200 && (
              <StarOutlined style={{ color: "#faad14", marginLeft: 8 }} />
            )} */}
          </div>
          {/* <Text type="secondary" style={{ fontSize: "12px" }}>
            {record.description}
          </Text> */}
        </div>
      ),
    },
    {
      title: "Language",
      dataIndex: "language",
      key: "language",
      render: (language) => (
        <Tag color="blue">{language?.toUpperCase() || "EN"}</Tag>
      ),
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      render: (category) => (
        <Tag color="purple">{category?.toUpperCase() || "UTILITY"}</Tag>
      ),
    },

    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status, record) => {
        const statusConfig = {
          approved: {
            color: "green",
            icon: <CheckCircleOutlined />,
            text: "APPROVED",
          },
          pending: {
            color: "orange",
            icon: <ClockCircleOutlined />,
            text: "PENDING",
          },
          rejected: {
            color: "red",
            icon: <CloseCircleOutlined />,
            text: "REJECTED",
          },
          discarded: {
            color: "gray",
            icon: <MinusCircleOutlined />,
            text: "DISCARDED",
          },
        };
        const config = statusConfig[status] || {
          color: "default",
          icon: <ClockCircleOutlined />,
          text: record.originalStatus || status?.toUpperCase() || "UNKNOWN",
        };

        return (
          <Tag color={config.color} icon={config.icon}>
            {config.text}
          </Tag>
        );
      },
    },

    // Default template toggle column (only for ADMIN users)
    ...(canSetDefaults ? [{
      title: (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          
          <span>Set Default</span>
          <Tooltip title="Set as default template (no limit)">
            <Text style={{ fontSize: 10, color: '#999' }}>
              ({templates.filter(t => t.defaulttemplate === true && t.status === 'active').length} active)
            </Text>
          </Tooltip>
        </div>
      ),
      key: "default",
      width: 100,
      render: (_, record) => {
        // Only show toggle for approved templates
        if (record.status !== 'approved') {
          return (
            <Tooltip title="Only approved templates can be set as default">
              <Switch disabled size="small" />
            </Tooltip>
          );
        }

        // Use the defaultemplate field directly from the template record
        const isDefault = record.defaultemplate === true;
        const isLoading = settingDefaultTemplate[record.id];

        return (
          <Tooltip title={isDefault ? "Remove from default templates" : "Set as default template"}>
            <Switch
              size="small"
              checked={isDefault}
              loading={isLoading}
              onChange={(checked) => handleSetDefaultTemplate(record.id, checked)}
              checkedChildren="✓"
              unCheckedChildren="○"
              style={{
                backgroundColor: isDefault ? '#10b981' : undefined
              }}
            />
          </Tooltip>
        );
      },
    }] : []),

    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (_, record) => {
        const menu = (
          <Menu>
            <Menu.Item
              key="preview"
              icon={<EyeOutlined />}
              onClick={() => handlePreviewTemplate(record)}
            >
              Preview
            </Menu.Item>
            {/* <Menu.Item key="broadcast" icon={<SendOutlined />}>
              Use in Broadcast
            </Menu.Item> */}
            <Menu.Divider />
            <Menu.Item
              key="delete"
              icon={<DeleteOutlined />}
              danger
              onClick={() => {
                Modal.confirm({
                  title: "Delete Template",
                  content:
                    "Are you sure you want to delete this template? This action cannot be undone.",
                  okText: "Yes, Delete",
                  okType: "danger",
                  cancelText: "Cancel",
                  onOk: () => handleDeleteTemplate(record.id),
                });
              }}
            >
              Delete
            </Menu.Item>
          </Menu>
        );

        return (
          <Dropdown overlay={menu} trigger={["click"]}>
            <Button type="text" icon={<MoreOutlined />} />
          </Dropdown>
        );
      },
    },
  ];

  const stats = {
    total: templates.length,
    active: templates.filter((t) => t.status === "active").length,
    inactive: templates.filter((t) => t.status === "inactive").length,
    totalUsage: templates.reduce((sum, t) => sum + t.usageCount, 0),
  };

  // Safely format API message payloads that may be objects
  const formatApiMessage = (msg) => {
    if (!msg) return "";
    if (typeof msg === "string") return msg;
    if (typeof msg === "object") {
      const { error, suggestion } = msg || {};
      const parts = [error, suggestion].filter(Boolean);
      if (parts.length) return parts.join(" — ");
      try {
        return JSON.stringify(msg);
      } catch (e) {
        return String(msg);
      }
    }
    return String(msg);
  };

  // Handle functions
  const handleCreateTemplate = async (values) => {
    setLoading(true);
    try {
      // Prepare request body according to API specification
      const requestBody = {
        templatename: values.name,
        templatelanguage: values.language || "en",
        templatecontent: values.content,
        templatecategory: values.category,
        companyid: currentUser?.companyid || currentUser?.companyId,
      };

      // Validate all required fields
      const missingFields = [];
      if (!requestBody.templatename) missingFields.push("Template Name");
      if (!requestBody.templatelanguage)
        missingFields.push("Template Language");
      if (!requestBody.templatecontent) missingFields.push("Template Content");
      if (!requestBody.templatecategory)
        missingFields.push("Template Category");
      if (!requestBody.companyid) missingFields.push("Company ID");

      if (missingFields.length > 0) {
        message.error(`Missing required fields: ${missingFields.join(", ")}`);
        setLoading(false);
        return;
      }

      // console.log("Request body:", requestBody);

      const response = await axiosConfig.post("/template/create", requestBody);

      if (response.data && response.data.status) {
        // Treat non-200 statuscodes as errors even if status is true
        if (String(response.data.statuscode) === "200") {
          setCreateTemplateVisible(false);
          createForm.resetFields();

          // Refresh the templates list
          await fetchTemplates(
            activeFilters,
            pagination.current,
            pagination.pageSize
          );
        } else {
          const errorMessage =
            formatApiMessage(response.data?.message) ||
            response.data?.error?.error?.message ||
            "Failed to create template";
          message.error(errorMessage);
        }
      } else {
        // Handle specific error messages from API
        const errorMessage =
          formatApiMessage(response.data?.message) || "Failed to create template";
        message.error(errorMessage);
        console.error("API Error Response:", response.data);
      }
    } catch (error) {
      console.error("Error creating template:", error);

      // Handle different types of errors
      if (error.response?.data) {
        const errorData = error.response.data;
        const errorMessage = formatApiMessage(errorData.message) || "Failed to create template";
        message.error(errorMessage);
        console.error("API Error Details:", errorData);
      } else {
        message.error("Network error. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEditTemplate = (template) => {
    setSelectedTemplate(template);
    editForm.setFieldsValue(template);
    setEditTemplateVisible(true);
  };

  const handleUpdateTemplate = async (values) => {
    setLoading(true);
    try {
      // Here you would make an API call to update the template
      // const response = await axios.put(`/api/template/update/${selectedTemplate.id}`, values);

      // For now, we'll simulate the API call and refresh the list
      setTimeout(async () => {
        setEditTemplateVisible(false);
        editForm.resetFields();
        setSelectedTemplate(null);
        setLoading(false);
        message.success("Template updated successfully");

        // Refresh the templates list
        await fetchTemplates(pagination.current, pagination.pageSize);
      }, 1000);
    } catch (error) {
      setLoading(false);
      message.error("Failed to update template");
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    try {
      // Prepare request body according to API specification
      const requestBody = {
        templateid: templateId,
      };

      // console.log("Deleting template with ID:", templateId);
      // console.log("Request body:", requestBody);

      const response = await axiosConfig.post("/template/delete", requestBody);

      if (response.data && response.data.status && response.data.statuscode !== 403) {
        message.success(
          response.data.message || "Template deleted successfully"
        );

        // Refresh the templates list
        await fetchTemplates(
          activeFilters,
          pagination.current,
          pagination.pageSize
        );
      } else {
        // Handle specific error messages from API
        const errorMessage =
          response.data?.message || "Failed to delete template";
        message.error(errorMessage);
        console.error("API Error Response:", response.data);
      }
    } catch (error) {
      console.error("Error deleting template:", error);

      // Handle different types of errors
      if (error.response?.data) {
        const errorData = error.response.data;
        const errorMessage = errorData.message || "Failed to delete template";
        message.error(errorMessage);
        console.error("API Error Details:", errorData);
      } else {
        message.error("Network error. Please try again.");
      }
    }
  };

  const handleDuplicateTemplate = async (template) => {
    try {
      // Here you would make an API call to duplicate the template
      // const duplicateData = {
      //   ...template,
      //   name: `${template.name} (Copy)`
      // };
      // const response = await axios.post('/api/template/duplicate', duplicateData);

      message.success("Template duplicated successfully");

      // Refresh the templates list
      await fetchTemplates(pagination.current, pagination.pageSize);
    } catch (error) {
      message.error("Failed to duplicate template");
    }
  };

  const handlePreviewTemplate = (template) => {
    setSelectedTemplate(template);
    setPreviewTemplateVisible(true);
  };

  const extractVariables = (content) => {
    const regex = /\{\{([^}]+)\}\}/g;
    const variables = [];
    let match;
    while ((match = regex.exec(content)) !== null) {
      if (!variables.includes(match[1])) {
        variables.push(match[1]);
      }
    }
    return variables;
  };

  /**
   * Extract footer and clean body from a template.
   * Priority: explicit FOOTER component > parse from templatecontent.
   * Returns { body, footer } where footer may be null.
   */
  const extractFooterAndBody = (template) => {
    const components = template.component || template.components || [];
    const footerComp = components.find(c => c.type === 'FOOTER');
    const bodyComp = components.find(c => c.type === 'BODY');

    // 1. Explicit FOOTER component in the component array — most reliable
    if (footerComp?.text) {
      const body = bodyComp?.text || template.templatecontent || template.content || '';
      return { body, footer: footerComp.text };
    }

    // 2. Compare templatecontent vs BODY component text.
    //    The backend puts the full text (body + footer) in templatecontent,
    //    but the BODY component only contains the actual body.
    //    The difference (trailing text in templatecontent after the body) is the footer.
    const fullText = template.templatecontent || template.content || '';
    const bodyText = bodyComp?.text || '';

    if (bodyText && fullText && fullText !== bodyText) {
      // Trim the body text to normalize trailing whitespace before comparing
      const trimmedBody = bodyText.trimEnd();
      const idx = fullText.indexOf(trimmedBody);
      if (idx !== -1) {
        const afterBody = fullText.substring(idx + trimmedBody.length).trim();
        // Only treat as footer if it looks like plain text — ignore JSON residue
        // (e.g. ","example":{...}} left over from a serialized component array)
        const looksLikeJson = /^[",\[{]/.test(afterBody) || afterBody.includes('"example"');
        if (afterBody.length > 0 && !looksLikeJson) {
          return { body: bodyText, footer: afterBody };
        }
      }
    }

    // 3. No footer detected — body is the full content
    return { body: bodyText || fullText, footer: null };
  };

  // Helper function to get template color based on category
  const getTemplateColor = (category) => {
    const colorMap = {
      'marketing': '#eb2f96',
      'utility': '#722ed1',
      'transactional': '#1890ff',
      'promotional': '#10b981',
      'support': '#f759ab',
      'greeting': '#13c2c2',
      'default': '#8c8c8c'
    };
    return colorMap[category?.toLowerCase()] || colorMap.default;
  };

  // Helper function to get template icon based on category
  const getTemplateIcon = (category) => {
    const iconMap = {
      'marketing': '🎯',
      'utility': '🔧',
      'transactional': '📦',
      'promotional': '🎉',
      'support': '💬',
      'greeting': '👋',
      'default': '📄'
    };
    return iconMap[category?.toLowerCase()] || iconMap.default;
  };

  /* DISABLED: Pre-approved templates functionality
  // Handle using pre-approved template (preview)
  const handleUsePreApprovedTemplate = (template) => {
    
    // Use the original template structure for preview if available
    const templateForPreview = template.originalTemplate ? {
      id: template.originalTemplate.templateid,
      name: template.originalTemplate.templatename,
      content: template.originalTemplate.templatecontent,
      component: template.originalTemplate.component,
      buttons: template.buttons || [],
      category: template.originalTemplate.templatecategory,
      language: template.originalTemplate.templatelanguage,
      status: 'approved',
      isPreApproved: true,
      variables: extractVariables(template.originalTemplate.templatecontent || '')
    } : {
      // Handle direct API template structure
      id: template.id,
      name: template.name,
      content: template.content,
      component: template.component,
      buttons: template.buttons || [],
      category: template.category,
      language: template.language,
      status: 'approved',
      isPreApproved: true,
      variables: extractVariables(template.content || '')
    };
    
    setSelectedTemplate(templateForPreview);
    setMobilePreviewVisible(true); // Use mobile preview like in template creation
  };

  // Handle creating template from pre-approved
  const handleCreateFromPreApproved = (template) => {
    // Navigate to the new template broadcast page with the selected template
    navigate('/dashboard/new-template-broadcast', {
      state: { template }
    });
  };
  */

  /* DISABLED: Pre-approved templates stats
  // Get category stats for pre-approved templates (including API defaults)
  const getPreApprovedStats = () => {
    const allTemplates = getFilteredPreApprovedTemplates();
    const categories = {};
    allTemplates.forEach(template => {
      categories[template.category] = (categories[template.category] || 0) + 1;
    });
    return {
      total: allTemplates.length,
      categories,
      totalUsage: allTemplates.reduce((sum, t) => sum + t.usageCount, 0),
      avgRating: allTemplates.length > 0 
        ? (allTemplates.reduce((sum, t) => sum + t.rating, 0) / allTemplates.length).toFixed(1)
        : '0.0'
    };
  };

  // Filter pre-approved templates (ONLY API default templates, no static data)
  const getFilteredPreApprovedTemplates = () => {
    // Use the already mapped defaultTemplates from the API (no need to re-map)
    const allTemplates = defaultTemplates || [];
    
    if (!searchText) return allTemplates;
    
    return allTemplates.filter(template => 
      template.name.toLowerCase().includes(searchText.toLowerCase()) ||
      template.description.toLowerCase().includes(searchText.toLowerCase()) ||
      template.category.toLowerCase().includes(searchText.toLowerCase()) ||
      template.tags.some(tag => tag.toLowerCase().includes(searchText.toLowerCase()))
    );
  };
  */

  // Helper function to get category color
  const getCategoryColor = (category) => {
    const colorMap = {
      'greeting': '#10b981',
      'transactional': '#1890ff',
      'utility': '#722ed1',
      'promotional': '#eb2f96',
      'support': '#f759ab',
      'marketing': '#13c2c2',
      'authentication': '#fa8c16'
    };
    return colorMap[category?.toLowerCase()] || '#10b981';
  };

  // Helper function to get category icon
  const getCategoryIcon = (category) => {
    const iconMap = {
      'greeting': '👋',
      'transactional': '📦',
      'utility': '⚙️',
      'promotional': '🎉',
      'support': '💬',
      'marketing': '📢',
      'authentication': '🔐'
    };
    return iconMap[category?.toLowerCase()] || '📝';
  };

  const renderTemplateMessageText = (content) => {
    if (!content) return 'No content available';

    const parts = String(content).split(/(\{\{[^}]+\}\})/g).filter(Boolean);
    return parts.map((part, index) => {
      if (/^\{\{[^}]+\}\}$/.test(part)) {
        return (
          <span key={`var-${index}`} className="template-variable-chip">
            {part}
          </span>
        );
      }
      return <React.Fragment key={`txt-${index}`}>{part}</React.Fragment>;
    });
  };

  const getTemplateUpdatedLabel = (template) => {
    if (template?.lastUsed) return `Updated ${template.lastUsed}`;
    if (template?.createdAt) return `Updated ${template.createdAt}`;
    return 'Updated recently';
  };

  const getRejectedTemplateReason = (template) => {
    if (template?.rejectionReason) return template.rejectionReason;
    if (template?.errorMessage) return template.errorMessage;
    return 'This template needs content or variable formatting changes before it can be approved.';
  };

  // WhatsApp Chat-Style Template Card Component
  const renderWhatsAppTemplateCard = (template, buttonText = "Use This Template", onButtonClick) => {
    const handleButtonClick = (e) => {
      e.stopPropagation();
      if (onButtonClick) {
        onButtonClick(template);
      }
    };

    // Extract template content — library templates use template_data; others use component[]
    const getTemplateContent = () => {
      // Library template: return body as-is to preserve {{n}} variables
      if (template.template_data?.body) {
        return template.template_data.body;
      }
      if (template.body) return template.body;
      if (template.content) return template.content;
      const bodyComponent = (template.components || template.component)?.find(c => c.type === 'BODY');
      if (bodyComponent?.text) return bodyComponent.text;
      return 'No content available';
    };

    // Extract footer text
    const getTemplateFooter = () => {
      if (template.footer) return template.footer;
      const footerComponent = (template.components || template.component)?.find(c => c.type === 'FOOTER');
      return footerComponent?.text || null;
    };

    // Extract header — library templates use template_data.header
    const getTemplateHeader = () => {
      if (template.template_data?.header) return template.template_data.header;
      if (template.header) return template.header;
      const headerComponent = (template.components || template.component)?.find(c => c.type === 'HEADER');
      return headerComponent?.text || null;
    };

    // Get header media info
    const getHeaderMedia = () => {
      const headerComponent = (template.components || template.component)?.find(c => c.type === 'HEADER');
      const format = template.header_format || headerComponent?.format || null;
      const mediaUrl = template.header_media_url || null;
      return { format, mediaUrl };
    };
    const headerMedia = getHeaderMedia();

    // Extract buttons — library templates use template_data.buttons
    const getTemplateButtons = () => {
      if (template.template_data?.buttons?.length > 0) return template.template_data.buttons;
      if (template.buttons && template.buttons.length > 0) return template.buttons;
      const buttonsComponent = (template.components || template.component)?.find(c => c.type === 'BUTTONS');
      return buttonsComponent?.buttons || [];
    };

    const templateContent = getTemplateContent();
    const templateHeader = getTemplateHeader();
    const templateFooter = getTemplateFooter();
    const templateButtons = getTemplateButtons();

    return (
      <div className="whatsapp-template-card template-card--library">
        <div className="template-card-topbar">
          <div className="template-card-category-pill">{(template.category || 'MARKETING').toUpperCase()}</div>
        </div>

        <div className="whatsapp-message-container">
          <div className="whatsapp-message-bubble">
            {headerMedia.mediaUrl && headerMedia.format === 'IMAGE' && (
              <div style={{ margin: '-12px -12px 8px -12px' }}>
                <img 
                  src={headerMedia.mediaUrl} 
                  alt="Template header" 
                  style={{ width: '100%', maxHeight: 180, objectFit: 'cover', display: 'block' }} 
                  onError={(e) => { e.target.parentElement.innerHTML = '<div style="padding:20px;text-align:center;color:#999;background:#e8f5e9">📷 Image</div>'; }}
                />
              </div>
            )}
            {headerMedia.mediaUrl && headerMedia.format === 'VIDEO' && (
              <div style={{ margin: '-12px -12px 8px -12px' }}>
                <video 
                  src={headerMedia.mediaUrl} 
                  style={{ width: '100%', maxHeight: 180, objectFit: 'cover', display: 'block' }} 
                  controls 
                  preload="metadata"
                />
              </div>
            )}
            {headerMedia.mediaUrl && headerMedia.format === 'DOCUMENT' && (
              <div style={{ margin: '0 0 8px 0', display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 24 }}>📄</span>
                <a href={headerMedia.mediaUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: '#1890ff', wordBreak: 'break-all' }}>View Document</a>
              </div>
            )}

            {/* Fallback placeholder when format exists but no URL */}
            {!headerMedia.mediaUrl && (headerMedia.format === 'IMAGE' || headerMedia.format === 'VIDEO' || headerMedia.format === 'DOCUMENT') && (
              <div style={{ margin: '-12px -12px 8px -12px', background: '#e8f5e9', padding: '20px', textAlign: 'center', color: '#999', fontSize: 13 }}>
                {headerMedia.format === 'IMAGE' && '📷 Image'}
                {headerMedia.format === 'VIDEO' && '🎥 Video'}
                {headerMedia.format === 'DOCUMENT' && '📄 Document'}
              </div>
            )}

            {!headerMedia.mediaUrl && !headerMedia.format && templateHeader && (
              <div className="whatsapp-message-header">
                {templateHeader}
              </div>
            )}

            <div className="whatsapp-message-body">
              {renderTemplateMessageText(templateContent)}
            </div>

            {templateFooter && (
              <div className="whatsapp-message-footer-copy">
                {templateFooter}
              </div>
            )}

            {templateButtons.length > 0 && (
              <div className="whatsapp-message-buttons">
                {templateButtons.map((button, index) => (
                  <div 
                    key={index}
                    className="whatsapp-button"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      justifyContent: 'center'
                    }}
                  >
                    {button.type === 'URL' && <LinkOutlined style={{ fontSize: '12px' }} />}
                    {button.type === 'PHONE_NUMBER' && <PhoneOutlined style={{ fontSize: '12px' }} />}
                    <span>{button.text}</span>
                  </div>
                ))}
              </div>
            )}

            <div className="whatsapp-timestamp">
              {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </div>
          </div>
        </div>

        <div className="template-card-meta">
          <div className="template-card-meta-name">{template.name || 'Template Name'}</div>
          <div className="template-card-meta-updated">{getTemplateUpdatedLabel(template)}</div>
        </div>

        <div className="whatsapp-template-actions">
          <Tooltip title={(!loadingWabaDetails && !hasWabaDetails && !wabaFetchError) ? "Set up WhatsApp Business first" : ""}>
            <Button 
              className="tpl-btn-primary"
              onClick={handleButtonClick}
              disabled={!loadingWabaDetails && !hasWabaDetails && !wabaFetchError}
              style={{
                fontWeight: 600,
                height: '36px',
                padding: '0 28px',
                borderRadius: '8px',
                fontSize: '13px'
              }}
            >
              {buttonText}
            </Button>
          </Tooltip>
        </div>
      </div>
    );
  };

  // DISABLED: Pre-approved templates rendering function - renderPreApprovedTemplates()
  // This function has been commented out to disable pre-approved templates functionality

  // Get status configuration for template cards
  const getStatusConfig = (status) => {
    const statusConfigs = {
      approved: {
        color: '#10b981',
        bgColor: '#f0fdf4',
        borderColor: '#b7ebc6',
        icon: <CheckCircleOutlined />,
        text: 'APPROVED',
        headerBg: 'linear-gradient(135deg, #0d7a66 0%, #128c7e 100%)'
      },
      pending: {
        color: '#f59e0b',
        bgColor: '#fffbeb',
        borderColor: '#ffd591',
        icon: <ClockCircleOutlined />,
        text: 'PENDING',
        headerBg: 'linear-gradient(135deg, #b36a08 0%, #ff9f43 100%)'
      },
      rejected: {
        color: '#ef4444',
        bgColor: '#fef2f2',
        borderColor: '#ffccc7',
        icon: <CloseCircleOutlined />,
        text: 'REJECTED',
        headerBg: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)'
      },
      discarded: {
        color: '#6b7280',
        bgColor: '#f9fafb',
        borderColor: '#d1d5db',
        icon: <MinusCircleOutlined />,
        text: 'DISCARDED',
        headerBg: 'linear-gradient(135deg, #4b5563 0%, #6b7280 100%)'
      }
    };
    return statusConfigs[status?.toLowerCase()] || statusConfigs.pending;
  };

  // Render individual template card for My Templates
  const renderMyTemplateCard = (template) => {
    const statusConfig = getStatusConfig(template.status);
    
    // Extract template content (use parsed body without footer when available)
    const getTemplateContent = () => {
      if (template.body) return template.body;
      if (template.content) return template.content;
      const bodyComponent = template.component?.find(c => c.type === 'BODY');
      if (bodyComponent?.text) return bodyComponent.text;
      return 'No content available';
    };

    // Extract footer
    const getTemplateFooter = () => {
      if (template.footer) return template.footer;
      const footerComponent = template.component?.find(c => c.type === 'FOOTER');
      return footerComponent?.text || null;
    };

    // Extract buttons from component structure
    const getTemplateButtons = () => {
      if (template.buttons && template.buttons.length > 0) return template.buttons;
      const buttonsComponent = template.component?.find(c => c.type === 'BUTTONS');
      return buttonsComponent?.buttons || [];
    };

    const templateContent = getTemplateContent();
    const templateFooter = getTemplateFooter();
    const templateButtons = getTemplateButtons();
    const primaryActionLabel = template.status === 'approved'
      ? 'Send'
      : template.status === 'rejected'
        ? 'Fix'
        : 'Preview';

    const handleCardClick = () => {
      setSelectedTemplate(template);
      setMobilePreviewVisible(true);
    };

    const handleBroadcastClick = (e) => {
      e.stopPropagation();
      
      if (template.status === 'approved') {
        navigate('/dashboard/new-template-broadcast', {
          state: { template }
        });
      }
    };

    const handleDeleteClick = (e) => {
      e.stopPropagation();
      Modal.confirm({
        title: "Delete Template",
        content: "Are you sure you want to delete this template? This action cannot be undone.",
        okText: "Yes, Delete",
        okType: "danger",
        cancelText: "Cancel",
        onOk: () => handleDeleteTemplate(template.id),
      });
    };

    const handlePrimaryAction = (e) => {
      e.stopPropagation();

      if (template.status === 'approved') {
        handleBroadcastClick(e);
        return;
      }

      if (template.status === 'rejected') {
        handleEditTemplate(template);
        return;
      }

      handleCardClick();
    };

    return (
      <div 
        className={`whatsapp-template-card template-card--my template-card--${template.status || 'pending'}`}
        style={{ 
          cursor: 'pointer',
          border: `1px solid ${statusConfig.borderColor}`,
          position: 'relative'
        }}
        onClick={handleCardClick}
      >
        <div className="template-card-topbar">
          <div className={`template-card-status-pill template-card-status-pill--${template.status || 'pending'}`}>
            <span className="template-card-status-dot" />
            <span>{statusConfig.text}</span>
          </div>
          <div className="template-card-category-pill">{(template.category || 'UTILITY').toUpperCase()}</div>
        </div>

        <div className="whatsapp-message-container" style={{ minHeight: 160 }}>
          <div className="whatsapp-message-bubble" style={{ overflow: 'hidden' }}>
            {(() => {
              const headerComponent = template.component?.find(c => c.type === 'HEADER');
              const format = template.header_format || headerComponent?.format;
              const mediaUrl = template.header_media_url;
              
              if (mediaUrl && format === 'IMAGE') {
                return (
                  <div style={{ margin: '-10px -12px 8px -12px' }}>
                    <img 
                      src={mediaUrl} 
                      alt="Template header" 
                      style={{ width: '100%', maxHeight: 160, objectFit: 'cover', display: 'block' }} 
                      onError={(e) => { e.target.parentElement.innerHTML = '<div style="padding:16px;text-align:center;color:#999;background:#e8f5e9">📷 Image</div>'; }}
                    />
                  </div>
                );
              }
              if (mediaUrl && format === 'VIDEO') {
                return (
                  <div style={{ margin: '-10px -12px 8px -12px' }}>
                    <video 
                      src={mediaUrl} 
                      style={{ width: '100%', maxHeight: 160, objectFit: 'cover', display: 'block' }} 
                      controls 
                      preload="metadata"
                    />
                  </div>
                );
              }
              if (mediaUrl && format === 'DOCUMENT') {
                return (
                  <div style={{ margin: '0 0 8px 0', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 20 }}>📄</span>
                    <a href={mediaUrl} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} style={{ fontSize: 12, color: '#1890ff' }}>View Document</a>
                  </div>
                );
              }

              if (headerComponent?.format === 'TEXT' && headerComponent?.text) {
                return (
                  <div className="whatsapp-message-header">
                    {headerComponent.text}
                  </div>
                );
              }

              // Fallback placeholder when format exists but no URL
              if (format === 'IMAGE' || format === 'VIDEO' || format === 'DOCUMENT') {
                return (
                  <div style={{ margin: '-10px -12px 8px -12px', background: '#e8f5e9', padding: '16px', textAlign: 'center', color: '#999', fontSize: 13 }}>
                    {format === 'IMAGE' && '📷 Image'}
                    {format === 'VIDEO' && '🎥 Video'}
                    {format === 'DOCUMENT' && '📄 Document'}
                  </div>
                );
              }
              return null;
            })()}

            <div className="whatsapp-message-body" style={{
              maxHeight: 80,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 4,
              WebkitBoxOrient: 'vertical',
              fontSize: '13px',
              lineHeight: '1.4'
            }}>
              {renderTemplateMessageText(templateContent)}
            </div>

            {templateFooter && (
              <div className="whatsapp-message-footer-copy whatsapp-message-footer-copy--compact">
                {templateFooter}
              </div>
            )}

            {templateButtons.length > 0 && (
              <div className="whatsapp-message-buttons" style={{ marginTop: 8 }}>
                {templateButtons.slice(0, 2).map((button, index) => (
                  <div 
                    key={index}
                    className="whatsapp-button"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      justifyContent: 'center',
                      padding: '6px 4px',
                      fontSize: '12px'
                    }}
                  >
                    {button.type === 'URL' && <LinkOutlined style={{ fontSize: '11px' }} />}
                    {button.type === 'PHONE_NUMBER' && <PhoneOutlined style={{ fontSize: '11px' }} />}
                    {button.type === 'QUICK_REPLY' && <MessageOutlined style={{ fontSize: '11px' }} />}
                    <span style={{ 
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      maxWidth: '120px'
                    }}>{button.text}</span>
                  </div>
                ))}
                {templateButtons.length > 2 && (
                  <div style={{ 
                    fontSize: '11px', 
                    color: '#53bdeb', 
                    textAlign: 'center',
                    padding: '4px'
                  }}>
                    +{templateButtons.length - 2} more
                  </div>
                )}
              </div>
            )}

            <div className="whatsapp-timestamp">
              {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </div>
          </div>
        </div>

        <div className="template-card-meta">
          <div className="template-card-meta-name">{template.name || 'Template Name'}</div>
          <div className="template-card-meta-updated">{getTemplateUpdatedLabel(template)}</div>
        </div>

        {template.status === 'rejected' && (
          <div className="template-card-error-alert">
            <ExclamationCircleOutlined className="template-card-error-icon" />
            <div className="template-card-error-content">
              <div className="template-card-error-title">Needs attention</div>
              <div className="template-card-error-text">{getRejectedTemplateReason(template)}</div>
            </div>
          </div>
        )}

        <div className="whatsapp-template-actions" style={{ 
          padding: '12px',
          display: 'flex',
          gap: 8,
          background: statusConfig.bgColor
        }}>
          <Tooltip title={template.status === 'approved' ? 'Use in Broadcast' : template.status === 'rejected' ? 'Fix template' : 'Preview template'}>
            <Button 
              className={template.status === 'approved' ? 'tpl-btn-primary' : template.status === 'rejected' ? 'tpl-btn-danger-outline' : 'tpl-btn-outline'}
              icon={template.status === 'approved' ? <SendOutlined /> : template.status === 'rejected' ? <EditOutlined /> : <EyeOutlined />}
              onClick={handlePrimaryAction}
              style={{ flex: 1 }}
            >
              {primaryActionLabel}
            </Button>
          </Tooltip>

          <Tooltip title="Preview template">
            <Button 
              icon={<EyeOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                handleCardClick();
              }}
            />
          </Tooltip>

          <Tooltip title="Edit template">
            <Button 
              icon={<EditOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                handleEditTemplate(template);
              }}
            />
          </Tooltip>
          
          <Tooltip title="Delete Template">
            <Button 
              icon={<DeleteOutlined />}
              danger
              onClick={handleDeleteClick}
            />
          </Tooltip>
        </div>
      </div>
    );
  };

  // Render my templates (card-based layout)
  const renderMyTemplates = () => {
    // Group templates by status for stats
    const templateStats = {
      total: filteredTemplates.length,
      approved: filteredTemplates.filter(t => t.status === 'approved').length,
      pending: filteredTemplates.filter(t => t.status === 'pending').length,
      rejected: filteredTemplates.filter(t => t.status === 'rejected').length,
      discarded: filteredTemplates.filter(t => t.status === 'discarded').length,
    };

    return (
      <div>
        {/* Show skeleton loading */}
        <BoneSkeleton name="my-templates" loading={tableLoading && (templates || []).length === 0} fallback={<MyTemplatesGridSkeleton />}>
          <>
            {/* Search and Actions Bar with inline stats */}
            <div
              className="template-search-actions-bar"
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: 20,
                gap: 10,
                flexWrap: "nowrap",
              }}
            >
              {/* Search input */}
              <div className="template-theme-input template-search-input" style={{ flexShrink: 0 }}>
                <Input
                  placeholder="Search my templates..."
                  prefix={
                    searchLoading ? <LoadingOutlined className="template-theme-icon" /> : <SearchOutlined className="template-theme-icon-grey" />
                  }
                  value={searchText}
                  onChange={(e) => handleSearch(e.target.value)}
                  style={{ width: 420, maxWidth: '100%' }}
                  allowClear
                  size="large"
                />
              </div>

              {/* Filter button */}
              <div style={{ flexShrink: 0 }}>
                <Badge
                  className="template-theme-badge"
                  count={
                    Object.values(activeFilters).filter(
                      (value) => value && value !== ""
                    ).length
                  }
                  size="small"
                >
                  <Button
                    className="template-theme-button"
                    icon={<FilterOutlined />}
                    onClick={handleAdvancedFilter}
                    size="large"
                    type={
                      Object.values(activeFilters).some(
                        (value) => value && value !== ""
                      )
                        ? "primary"
                        : "default"
                    }
                  >
                    Filter
                  </Button>
                </Badge>
              </div>

              {/* Compact inline stats */}
              <div className="template-stats-container" style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 8,
                padding: '6px 12px',
                background: '#fafafa',
                borderRadius: 8,
                border: '1px solid #e8e8e8',
                flexShrink: 0,
              }}>
                <Tooltip title="Total Templates">
                  <Tag 
                    icon={<FileTextOutlined style={{ fontSize: 13 }} />} 
                    color="blue"
                    style={{ 
                      margin: 0, 
                      cursor: 'default',
                      fontSize: 13,
                      fontWeight: 600,
                      padding: '3px 8px',
                      height: 26,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 5
                    }}
                  >
                    {templateStats.total}
                  </Tag>
                </Tooltip>
                <Tooltip title="Approved">
                  <Tag 
                    icon={<CheckCircleOutlined style={{ fontSize: 13 }} />} 
                    color="success"
                    style={{ 
                      margin: 0, 
                      cursor: 'default',
                      fontSize: 13,
                      fontWeight: 600,
                      padding: '3px 8px',
                      height: 26,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 5
                    }}
                  >
                    {templateStats.approved}
                  </Tag>
                </Tooltip>
                <Tooltip title="Pending">
                  <Tag 
                    icon={<ClockCircleOutlined style={{ fontSize: 13 }} />} 
                    color="warning"
                    style={{ 
                      margin: 0, 
                      cursor: 'default',
                      fontSize: 13,
                      fontWeight: 600,
                      padding: '3px 8px',
                      height: 26,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 5
                    }}
                  >
                    {templateStats.pending}
                  </Tag>
                </Tooltip>
                <Tooltip title="Rejected">
                  <Tag 
                    icon={<CloseCircleOutlined style={{ fontSize: 13 }} />} 
                    color="error"
                    style={{ 
                      margin: 0, 
                      cursor: 'default',
                      fontSize: 13,
                      fontWeight: 600,
                      padding: '3px 8px',
                      height: 26,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 5
                    }}
                  >
                    {templateStats.rejected}
                  </Tag>
                </Tooltip>
              </div>

              {/* Spacer pushes Refresh to the right */}
              <div style={{ flex: 1 }} />

              {/* Refresh button */}
              <Button
                className="template-theme-button"
                icon={<ReloadOutlined />}
                onClick={handleRefresh}
                disabled={tableLoading || refreshCooldown}
                title={refreshCooldown ? 'Please wait before refreshing again' : 'Refresh templates'}
                size="large"
                style={{ flexShrink: 0 }}
              >
                Refresh
              </Button>
            </div>

            {/* Templates Grid */}
            <BoneSkeleton name="my-templates-grid" loading={tableLoading} fallback={<LibraryGridSkeleton />}>
              {filteredTemplates.length === 0 ? (
              <div className="template-empty-state">
                <div className="template-empty-state-icon">
                  <PlusOutlined />
                </div>
                <div className="template-empty-state-title">
                  {searchText ? 'No templates found' : 'Create your first template'}
                </div>
                <div className="template-empty-state-copy">
                  {searchText
                    ? 'Try adjusting your search or filters to find a template.'
                    : 'Start with a WhatsApp-ready template card and manage approvals, variables, and actions visually.'}
                </div>
                {!searchText && (
                  <Button 
                    type="primary" 
                    className="btn-create-template"
                    icon={<PlusOutlined />}
                    onClick={() => setShowAdvancedBuilder(true)}
                    disabled={!loadingWabaDetails && !hasWabaDetails && !wabaFetchError}
                  >
                    Create Template
                  </Button>
                )}
              </div>
            ) : (
              <>
                <Row gutter={[16, 16]}>
                  {filteredTemplates.map((template) => (
                    <Col xs={24} sm={12} lg={8} xl={6} key={template.id}>
                      {renderMyTemplateCard(template)}
                    </Col>
                  ))}
                </Row>

                {/* Pagination */}
                {pagination.total > pagination.pageSize && (
                  <div style={{ 
                    marginTop: 24, 
                    textAlign: 'center',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: 16
                  }}>
                    <Text type="secondary">
                      Showing {((pagination.current - 1) * pagination.pageSize) + 1} - {Math.min(pagination.current * pagination.pageSize, pagination.total)} of {pagination.total} templates
                    </Text>
                    <Space>
                      <Button
                        disabled={pagination.current === 1}
                        onClick={() => handleTableChange({ current: pagination.current - 1, pageSize: pagination.pageSize })}
                      >
                        Previous
                      </Button>
                      <Button
                        disabled={pagination.current * pagination.pageSize >= pagination.total}
                        onClick={() => handleTableChange({ current: pagination.current + 1, pageSize: pagination.pageSize })}
                      >
                        Next
                      </Button>
                    </Space>
                  </div>
                )}
              </>
            )}
            </BoneSkeleton>
          </>
        </BoneSkeleton>
      </div>
    );
  };

  // Render Template Library
  const renderTemplateLibrary = () => {
    const categoryTabs = [
      { key: 'all', label: 'All' },
      { key: 'AUTHENTICATION', label: 'Authentication' },
      { key: 'UTILITY', label: 'Utility' },
    ];

    // Derive topic options from categoryData based on active category
    const topicOptions = libraryActiveCategory === 'all'
      ? [...new Set([...categoryData.UTILITY.topics, ...categoryData.AUTHENTICATION.topics])]
      : (categoryData[libraryActiveCategory]?.topics || []);

    return (
      <div>
        {/* Category Tabs + Topic Filter + Search — single row */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 12,
          marginBottom: 20,
        }}>
          {/* Category Tabs */}
          <Space>
            {categoryTabs.map(tab => (
              <Button
                key={tab.key}
                type={libraryActiveCategory === tab.key ? 'primary' : 'default'}
                onClick={() => setLibraryActiveCategory(tab.key)}
                style={{
                  borderRadius: 20,
                  fontWeight: libraryActiveCategory === tab.key ? 600 : 400,
                  background: libraryActiveCategory === tab.key ? '#0036b0' : undefined,
                  borderColor: libraryActiveCategory === tab.key ? '#0036b0' : undefined,
                }}
              >
                {tab.label}
              </Button>
            ))}
          </Space>

          {/* Topic Filter + Search */}
          <Space>
            <Select
              placeholder="Filter by topic"
              value={libraryTopic || undefined}
              allowClear
              style={{ width: 200 }}
              onChange={(value) => {
                const newTopic = value || '';
                setLibraryTopic(newTopic);
                setLibraryPage(1);
                fetchLibraryTemplates(1, newTopic);
              }}
              onClear={() => {
                setLibraryTopic('');
                setLibraryPage(1);
                fetchLibraryTemplates(1, '');
              }}
            >
              {topicOptions.map(topic => (
                <Option key={topic} value={topic}>{topic}</Option>
              ))}
            </Select>
            <Input
              placeholder="Search templates..."
              prefix={<SearchOutlined />}
              value={librarySearch}
              onChange={(e) => handleLibrarySearch(e.target.value)}
              style={{ width: 260 }}
              allowClear
            />
          </Space>
        </div>

        {/* Template Cards */}
        {libraryActiveCategory && (
          <div>
            {libraryLoading && libraryTemplates.length === 0 ? (
              <div style={{ marginBottom: 24 }}>
                <LibraryGridSkeleton />
              </div>
            ) : libraryTemplates.length === 0 ? (
              <Empty
                description="No templates found"
                style={{ margin: '40px 0' }}
              />
            ) : (
              <>
                <Row gutter={[16, 16]}>
                  {libraryTemplates.map((template) => (
                    <Col xs={24} sm={12} lg={8} xl={6} key={template.id}>
                      {renderWhatsAppTemplateCard(
                        template, 
                        "Use This Template", 
                        () => handleUseLibraryTemplate(template)
                      )}
                    </Col>
                  ))}
                </Row>

                {libraryPagination.total > LIBRARY_PAGE_SIZE && (
                  <div style={{ 
                    marginTop: 24, 
                    textAlign: 'center',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: 16
                  }}>
                    <Text type="secondary">
                      Showing {((libraryPage - 1) * LIBRARY_PAGE_SIZE) + 1} - {Math.min(libraryPage * LIBRARY_PAGE_SIZE, libraryPagination.total)} of {libraryPagination.total} templates
                    </Text>
                    <Space>
                      <Button
                        disabled={libraryPage === 1}
                        onClick={() => {
                          setLibraryPage(libraryPage - 1);
                          fetchLibraryTemplates(libraryPage - 1);
                        }}
                      >
                        Previous
                      </Button>
                      <Button
                        disabled={libraryPage * LIBRARY_PAGE_SIZE >= libraryPagination.total}
                        onClick={() => {
                          setLibraryPage(libraryPage + 1);
                          fetchLibraryTemplates(libraryPage + 1);
                        }}
                      >
                        Next
                      </Button>
                    </Space>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="template-page">
      {/* WABA Setup Notice - Only show when API succeeded but no WABA data */}
      {!loadingWabaDetails && !hasWabaDetails && !wabaFetchError && (
        <div 
          style={{
            marginBottom: 16,
            padding: '10px 16px',
            background: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: 8,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 8
          }}
        >
          <span style={{ color: '#475569', fontSize: 14 }}>
            <InfoCircleOutlined style={{ marginRight: 8, color: '#64748b' }} />
            Complete WhatsApp Business setup to manage templates
          </span>
          <a 
            onClick={() => navigate('/whatsappregistration')}
            style={{ 
              color: '#3b82f6', 
              fontSize: 14, 
              fontWeight: 500,
              cursor: 'pointer',
              whiteSpace: 'nowrap'
            }}
          >
            Set up now →
          </a>
        </div>
      )}

      {showAdvancedBuilder ? (
        <AdvancedTemplateBuilder
          onBack={() => setShowAdvancedBuilder(false)}
          onSave={async (templateData) => {
            try {
              // console.log("Template data from builder:", templateData);

              // Helper function to validate package name format
              const validatePackageName = (packageName) => {
                if (!packageName || !packageName.trim()) {
                  return { isValid: false, error: "Package name is required" };
                }

                // Check for minimum segments (at least 2 parts separated by dots)
                const segments = packageName.split(".");
                if (segments.length < 2) {
                  return {
                    isValid: false,
                    error:
                      "Per Android guidelines, an application ID must have at least two segments, separated by dots",
                  };
                }

                // Validate each segment
                for (let i = 0; i < segments.length; i++) {
                  const segment = segments[i];

                  // Check if segment is empty
                  if (!segment) {
                    return {
                      isValid: false,
                      error: "Package name segments cannot be empty",
                    };
                  }

                  // Check if segment starts with a letter
                  if (!/^[a-zA-Z]/.test(segment)) {
                    return {
                      isValid: false,
                      error: "Each segment should begin with a letter",
                    };
                  }

                  // Check if all characters in segment are alphanumeric or underscore
                  if (!/^[a-zA-Z][a-zA-Z0-9_]*$/.test(segment)) {
                    return {
                      isValid: false,
                      error:
                        "All characters in a segment should be alphanumeric or underscore",
                    };
                  }
                }

                return { isValid: true, error: null };
              };

              // Helper function to validate signature hash format
              const validateSignatureHash = (signatureHash) => {
                if (!signatureHash || !signatureHash.trim()) {
                  return {
                    isValid: false,
                    error: "App signature hash is required",
                  };
                }

                if (signatureHash.length !== 11) {
                  return {
                    isValid: false,
                    error: "Your signature hash must be 11 characters long",
                  };
                }

                // Check if it contains only valid base64 characters
                const base64Regex = /^[A-Za-z0-9+/=]+$/;
                if (!base64Regex.test(signatureHash)) {
                  return {
                    isValid: false,
                    error:
                      "Signature hash must contain only valid base64 characters (A-Z, a-z, 0-9, +, /, =)",
                  };
                }

                return { isValid: true, error: null };
              };

              // Helper function to validate variable format based on type
              const validateVariableFormat = (content, variableType) => {
                const variables = [];
                let match;

                if (variableType === "number") {
                  // For number format: {{1}}, {{2}}, {{3}}, etc.
                  const variableRegex = /\{\{(\d+)\}\}/g;

                  while ((match = variableRegex.exec(content)) !== null) {
                    const varNum = parseInt(match[1]);
                    if (!variables.includes(varNum)) {
                      variables.push(varNum);
                    }
                  }

                  // Variables should be sequential starting from 1
                  variables.sort((a, b) => a - b);
                  for (let i = 0; i < variables.length; i++) {
                    if (variables[i] !== i + 1) {
                      throw new Error(
                        `Variables must be sequential starting from {{1}}. Found {{${
                          variables[i]
                        }}} but expected {{${i + 1}}}`
                      );
                    }
                  }
                } else if (variableType === "name") {
                  // For name format: {{name}}, {{customer_name}}, etc.
                  const variableRegex = /\{\{([a-z0-9_]*[a-z][a-z0-9_]*)\}\}/g;

                  while ((match = variableRegex.exec(content)) !== null) {
                    const varName = match[1];
                    if (!variables.includes(varName)) {
                      variables.push(varName);
                    }
                  }

                  // For name format, validate that each variable follows naming rules
                  variables.forEach((variable) => {
                    const nameRegex = /^[a-z0-9_]*[a-z][a-z0-9_]*$/;
                    if (!nameRegex.test(variable)) {
                      throw new Error(
                        `Invalid variable name: {{${variable}}}. Variable names must contain lowercase letters, numbers, and underscores, and must include at least one letter.`
                      );
                    }
                  });
                }

                return variables;
              };

              // Helper function to validate URL format
              const validateUrl = (url) => {
                const httpsRegex = /^https:\/\/[^\s$.?#].[^\s]*$/i;
                return httpsRegex.test(url);
              };

              const getCreatedFlowButton = () => {
                const directButton = templateData.flowTemplateButton;
                const componentButton = templateData.flowButtonComponent?.buttons?.find(
                  (button) => String(button?.type || '').toUpperCase() === 'FLOW'
                );
                return directButton || componentButton || null;
              };

              // Helper function to validate variable examples
              const validateVariableExamples = (
                variables,
                examples,
                variableType
              ) => {
                if (
                  !examples ||
                  !examples.body_text ||
                  !Array.isArray(examples.body_text)
                ) {
                  throw new Error(
                    "Examples are required for templates with variables"
                  );
                }

                const exampleArray = examples.body_text[0];
                if (!Array.isArray(exampleArray)) {
                  throw new Error("Example format is invalid");
                }

                if (variables.length !== exampleArray.length) {
                  throw new Error(
                    `Number of variables (${variables.length}) must match number of examples (${exampleArray.length})`
                  );
                }

                // Validate example types based on variable type
                exampleArray.forEach((example, index) => {
                  // Basic validation - examples should be non-empty strings
                  if (!example || example.toString().trim() === "") {
                    throw new Error(
                      `Example for variable {{${index + 1}}} cannot be empty`
                    );
                  }

                  // For WhatsApp templates, example values can be any string content
                  // regardless of whether the variable format is number ({{1}}) or name ({{customer_name}})
                  // Examples: "John Doe", "Order #12345", "Premium Package", etc.

                  const exampleStr = example.toString().trim();

                  // Check minimum length
                  if (exampleStr.length < 1) {
                    throw new Error(
                      `Example for variable {{${
                        index + 1
                      }}} must be at least 1 character long`
                    );
                  }

                  // Check maximum length (WhatsApp limit)
                  if (exampleStr.length > 100) {
                    throw new Error(
                      `Example for variable {{${
                        index + 1
                      }}} must be less than 100 characters`
                    );
                  }

                  // Allow any reasonable text content - no strict format restrictions
                  // This supports examples like: "John Doe", "Order #12345", "Premium Plan", "99.99", etc.
                });

                return true;
              };

              // Extract content from template components
              let templateContent = "";
              let components = [];

              // Check if it's an Authentication template
              const isAuthentication =
                templateData.category === "Authentication";

              if (isAuthentication) {
                // For Authentication templates, use the specific structure from reference
                templateContent = "{{1}} is your verification code.";

                // Add optional content based on authentication settings
                if (templateData.authentication?.securityRecommendation) {
                  templateContent += " For your security, do not share this code.";
                }



                // Build components array for Authentication template with your specific structure
                components = [
                  {
                    type: "BODY",
                    add_security_recommendation:
                      templateData.authentication?.securityRecommendation ||
                      false,
                  },
                ];

                // Add FOOTER component if code expiration is enabled
                if (templateData.authentication?.codeExpiration) {
                  components.push({
                    type: "FOOTER",
                    code_expiration_minutes:
                      templateData.authentication?.expirationTime || 10,
                  });
                }

                // Add BUTTONS component based on code delivery type
                const buttonType =
                  templateData.authentication?.codeDelivery || "copy-code";
                let otpButtonType = "COPY_CODE"; // Default

                if (buttonType === "zero-tap") {
                  otpButtonType = "ZERO_TAP";
                } else if (buttonType === "one-tap") {
                  otpButtonType = "ONE_TAP";
                } else {
                  otpButtonType = "COPY_CODE";
                }

                // Build the OTP button object
                const otpButton = {
                  type: "OTP",
                  otp_type: otpButtonType,
                };

                // Add supported_apps for ONE_TAP and ZERO_TAP
                if (
                  otpButtonType === "ONE_TAP" ||
                  otpButtonType === "ZERO_TAP"
                ) {
                  const apps = templateData.authentication?.apps || [];
                  if (apps.length > 0) {
                    otpButton.supported_apps = apps.map((app) => ({
                      package_name: app.packageName,
                      signature_hash: app.signatureHash,
                    }));
                  }
                }

                // Add button text customization
                if (
                  templateData.authentication?.autofillButtonText &&
                  buttonType !== "copy-code"
                ) {
                  otpButton.autofill_text =
                    templateData.authentication.autofillButtonText;
                }

                if (templateData.authentication?.copyButtonText) {
                  otpButton.copy_code_text =
                    templateData.authentication.copyButtonText;
                }

                components.push({
                  type: "BUTTONS",
                  buttons: [otpButton],
                });

                // // Debug logging for authentication templates
                // console.log("Authentication template - Button type:", buttonType);
                // console.log("Authentication template - OTP button type:", otpButtonType);
                // console.log("Authentication template - Apps configuration:", templateData.authentication?.apps);
                // console.log("Authentication template - Final components:", JSON.stringify(components, null, 2));
              } else {
                // Build template content and components for non-authentication templates
                let bodyContent = "";
                let headerContent = "";

                if (templateData.components?.header?.content) {
                  headerContent = templateData.components.header.content;
                  templateContent += headerContent + "\n\n";
                }

                if (templateData.components?.body?.content) {
                  bodyContent = templateData.components.body.content;
                  templateContent += bodyContent;
                }

                if (templateData.components?.footer?.content) {
                  templateContent +=
                    "\n\n" + templateData.components.footer.content;
                }

                // If no content is built from components, use direct content
                if (!templateContent.trim() && templateData.templatecontent) {
                  bodyContent = templateData.templatecontent;
                  templateContent = templateData.templatecontent;
                }

                // Validate and build HEADER component if it exists and is text type
                if (
                  templateData.components?.header?.type === "text" &&
                  headerContent
                ) {
                  const headerVariables = validateVariableFormat(
                    headerContent,
                    templateData.variableType || "number"
                  );

                  // Check if examples are provided for all header variables
                  if (headerVariables.length > 0) {
                    const headerSamples =
                      templateData.components?.header?.variableSamples || {};
                    const missingHeaderExamples = [];

                    headerVariables.forEach((variable) => {
                      if (
                        !headerSamples[variable] ||
                        headerSamples[variable].trim() === ""
                      ) {
                        missingHeaderExamples.push(variable);
                      }
                    });

                    if (missingHeaderExamples.length > 0) {
                      throw new Error(
                        `Example content is required for header variables: ${missingHeaderExamples
                          .map((v) => `{{${v}}}`)
                          .join(", ")}`
                      );
                    }
                  }

                  // Build HEADER component
                  const headerComponent = {
                    type: "HEADER",
                    format: "TEXT",
                    text: headerContent,
                  };

                  // Add examples if variables exist
                  if (headerVariables.length > 0) {
                    let headerExamples = [];

                    // Use actual sample values from template builder if available
                    if (templateData.components?.header?.variableSamples) {
                      const headerSamples =
                        templateData.components.header.variableSamples;

                      // Create examples array based on variable order
                      if (templateData.variableType === "number") {
                        // For number format, variables are ordered numerically (1, 2, 3...)
                        const sortedVariables = headerVariables.sort(
                          (a, b) => parseInt(a) - parseInt(b)
                        );
                        headerExamples = sortedVariables.map(
                          (varNum) => headerSamples[varNum] || `Sample${varNum}`
                        );
                      } else {
                        // For name format, use the order they appear in content
                        headerExamples = headerVariables.map(
                          (variable) =>
                            headerSamples[variable] || `Sample_${variable}`
                        );
                      }
                    } else {
                      // Fallback to default examples if no samples provided
                      headerExamples = headerVariables.map((varNum, index) => {
                        if (templateData.variableType === "number") {
                          return index === 0
                            ? "Sample Header"
                            : `Header${index + 1}`;
                        } else {
                          return index === 0
                            ? "Sample Header"
                            : `Header_${index + 1}`;
                        }
                      });
                    }

                    headerComponent.example = {
                      header_text: headerExamples,
                    };

                    // Validate examples
                    validateVariableExamples(
                      headerVariables,
                      { body_text: [headerExamples] },
                      templateData.variableType || "number"
                    );
                  }

                  components.push(headerComponent);
                }

                // Validate variables in body content
                if (bodyContent) {
                  const variables = validateVariableFormat(
                    bodyContent,
                    templateData.variableType || "number"
                  );

                  // Check if examples are provided for all variables
                  if (variables.length > 0) {
                    const bodySamples =
                      templateData.components?.body?.variableSamples || {};
                    const missingExamples = [];

                    variables.forEach((variable) => {
                      if (
                        !bodySamples[variable] ||
                        bodySamples[variable].trim() === ""
                      ) {
                        missingExamples.push(variable);
                      }
                    });

                    if (missingExamples.length > 0) {
                      throw new Error(
                        `Example content is required for variables: ${missingExamples
                          .map((v) => `{{${v}}}`)
                          .join(", ")}`
                      );
                    }
                  }

                  // Build BODY component
                  const bodyComponent = {
                    type: "BODY",
                    text: bodyContent,
                  };

                  // Add examples if variables exist
                  if (variables.length > 0) {
                    let examples = [];

                    // Use actual sample values from template builder if available
                    if (templateData.components?.body?.variableSamples) {
                      const bodySamples =
                        templateData.components.body.variableSamples;

                      // Create examples array based on variable order
                      if (templateData.variableType === "number") {
                        // For number format, variables are ordered numerically (1, 2, 3...)
                        const sortedVariables = variables.sort(
                          (a, b) => parseInt(a) - parseInt(b)
                        );
                        examples = sortedVariables.map(
                          (varNum) => bodySamples[varNum] || `Sample${varNum}`
                        );
                      } else {
                        // For name format, use the order they appear in content
                        examples = variables.map(
                          (variable) =>
                            bodySamples[variable] || `Sample_${variable}`
                        );
                      }
                    } else {
                      // Fallback to default examples if no samples provided
                      examples = variables.map((varNum, index) => {
                        if (templateData.variableType === "number") {
                          return index === 0
                            ? "123456"
                            : `${860198 + index}-${230332 + index}`;
                        } else {
                          return index === 0 ? "Pablo" : `Customer${index + 1}`;
                        }
                      });
                    }

                    bodyComponent.example = {
                      body_text: [examples],
                    };

                    // Validate examples
                    validateVariableExamples(
                      variables,
                      bodyComponent.example,
                      templateData.variableType || "number"
                    );
                  }

                  components.push(bodyComponent);
                }

                // Add FOOTER component if footer content exists
                if (templateData.components?.footer?.content) {
                  components.push({
                    type: "FOOTER",
                    text: templateData.components.footer.content,
                  });
                }

                // Add BUTTONS component if buttons exist
                if (
                  templateData.components?.buttons &&
                  templateData.components.buttons.length > 0
                ) {
                  const buttons = templateData.components.buttons.map(
                    (button) => {
                      // Validate button structure
                      if (!button.text || button.text.trim() === "") {
                        throw new Error("Button text is required");
                      }

                      if (button.type === "url") {
                        if (!button.url || !validateUrl(button.url)) {
                          throw new Error(
                            `Button URL must be a valid HTTPS URL. Found: ${button.url}`
                          );
                        }
                        return {
                          type: "URL",
                          text: button.text,
                          url: button.url,
                        };
                      } else if (button.type === "phone") {
                        button.phoneNumber = button.phone || "";

                        if (!button.phoneNumber) {
                          throw new Error(
                            "Phone number is required for phone button"
                          );
                        }
                        return {
                          type: "PHONE_NUMBER",
                          text: button.text,
                          phone_number: button.phoneNumber,
                        };
                      } else if (button.type === "quick_reply") {
                        return {
                          type: "QUICK_REPLY",
                          text: button.text,
                        };
                      } else if (button.type === "flow") {
                        const flowButton = getCreatedFlowButton();
                        if (!flowButton) {
                          throw new Error("Create the WhatsApp Flow in Meta before submitting the template.");
                        }
                        if (!flowButton.flow_id && !flowButton.flow_name && !flowButton.flow_json) {
                          throw new Error("Flow button must include a Flow ID, Flow name, or Flow JSON.");
                        }
                        return {
                          ...flowButton,
                          type: "FLOW",
                          text: flowButton.text || button.text,
                        };
                      }
                      throw new Error(`Invalid button type: ${button.type}`);
                    }
                  );

                  const flowButtonCount = buttons.filter(
                    (button) => String(button?.type || '').toUpperCase() === 'FLOW'
                  ).length;
                  if (flowButtonCount > 1) {
                    throw new Error("Only one Flow button is allowed per template.");
                  }

                  components.push({
                    type: "BUTTONS",
                    buttons: buttons,
                  });
                }
              }
              components = components.map((obj) => {
                if (obj.type === "BUTTONS" && Array.isArray(obj.buttons)) {
                  obj.buttons = obj.buttons.map((button) => {
                    if (button.copy_code_text === "Copy code") {
                      const { copy_code_text, ...rest } = button;
                      return rest; // remove copy_code_text
                    }
                    return button;
                  });
                }
                return obj;
              });

              // console.log(components);
              // Prepare request body according to Meta WhatsApp API specification
              const requestBody = {
                templatename: templateData.name || templateData.templateName,
                templatelanguage: templateData.language || "en",
                templatecontent: templateContent.trim(),
                templatecategory: templateData.category,
                companyid: currentUser?.companyid || currentUser?.companyId,
                component: components,
              };

              // Add Meta WhatsApp specific fields
              if (templateData.variableType) {
                requestBody.variableType = templateData.variableType;
              }

              // Add authentication template specific fields
              if (templateData.category === "Authentication") {
                // Add validity period for authentication templates
                if (templateData.validityPeriod?.enabled) {
                  requestBody.validityPeriod = {
                    value: templateData.validityPeriod.value,
                    unit: templateData.validityPeriod.unit,
                  };
                }

                // Add authentication-specific configuration
                if (templateData.authentication) {
                  requestBody.authentication = {
                    codeDelivery: templateData.authentication.codeDelivery,
                    securityRecommendation:
                      templateData.authentication.securityRecommendation,
                    codeExpiration: templateData.authentication.codeExpiration,
                    expirationTime: templateData.authentication.expirationTime,
                    autofillButtonText:
                      templateData.authentication.autofillButtonText,
                    copyButtonText: templateData.authentication.copyButtonText,
                  };

                  // Add app configuration for zero-tap and one-tap
                  if (
                    templateData.authentication.codeDelivery !== "copy-code" &&
                    templateData.authentication.apps
                  ) {
                    requestBody.authentication.apps =
                      templateData.authentication.apps.filter(
                        (app) => app.packageName && app.signatureHash
                      );
                  }

                  // Add zero-tap agreement confirmation
                  if (templateData.authentication.codeDelivery === "zero-tap") {
                    requestBody.authentication.zeroTapAgreement =
                      templateData.authentication.zeroTapAgreement;
                  }
                }
              } else if (
                templateData.category === "Utility" &&
                templateData.validityPeriod?.enabled
              ) {
                requestBody.validityPeriod = {
                  value: templateData.validityPeriod.value,
                  unit: templateData.validityPeriod.unit,
                };
              }

              // Validate all required fields
              const missingFields = [];
              if (!requestBody.templatename)
                missingFields.push("Template Name");
              if (!requestBody.templatelanguage)
                missingFields.push("Template Language");
              if (!requestBody.templatecontent)
                missingFields.push("Template Content");
              if (!requestBody.templatecategory)
                missingFields.push("Template Category");
              if (!requestBody.companyid) missingFields.push("Company ID");
              if (
                !requestBody.component ||
                requestBody.component.length === 0
              ) {
                missingFields.push("Template Components");
              }

              // Additional validations
              if (
                requestBody.templatename &&
                !/^[a-z0-9_]+$/.test(requestBody.templatename)
              ) {
                throw new Error(
                  "Template name must contain only lowercase letters, numbers, and underscores"
                );
              }

              // Authentication template specific validations
              if (templateData.category === "Authentication") {
                // Validate zero-tap agreement
                if (
                  templateData.authentication?.codeDelivery === "zero-tap" &&
                  !templateData.authentication?.zeroTapAgreement
                ) {
                  throw new Error(
                    "Zero-tap agreement must be accepted for zero-tap authentication templates"
                  );
                }

                // Validate app configuration for zero-tap and one-tap
                if (templateData.authentication?.codeDelivery !== "copy-code") {
                  const apps = templateData.authentication?.apps || [];
                  if (apps.length === 0) {
                    throw new Error(
                      "At least one app configuration is required for zero-tap and one-tap authentication"
                    );
                  }

                  // Validate each app configuration with proper validation functions
                  apps.forEach((app, index) => {
                    if (!app.packageName || !app.signatureHash) {
                      throw new Error(
                        `App ${
                          index + 1
                        }: Package name and signature hash are required`
                      );
                    }

                    // Validate package name format
                    const packageValidation = validatePackageName(
                      app.packageName
                    );
                    if (!packageValidation.isValid) {
                      throw new Error(
                        `App ${index + 1} - Package Name: ${
                          packageValidation.error
                        }`
                      );
                    }

                    // Validate signature hash format
                    const hashValidation = validateSignatureHash(
                      app.signatureHash
                    );
                    if (!hashValidation.isValid) {
                      throw new Error(
                        `App ${index + 1} - Signature Hash: ${
                          hashValidation.error
                        }`
                      );
                    }
                  });
                }

                // Validate expiration time
                if (templateData.authentication?.codeExpiration) {
                  const expirationTime =
                    templateData.authentication?.expirationTime;
                  if (
                    !expirationTime ||
                    expirationTime < 1 ||
                    expirationTime > 90
                  ) {
                    throw new Error(
                      "Code expiration time must be between 1 and 90 minutes"
                    );
                  }
                }

                // Validate button texts
                if (
                  templateData.authentication?.autofillButtonText &&
                  templateData.authentication.autofillButtonText.length > 25
                ) {
                  throw new Error(
                    "Autofill button text must be 25 characters or less"
                  );
                }

                if (
                  templateData.authentication?.copyButtonText &&
                  templateData.authentication.copyButtonText.length > 25
                ) {
                  throw new Error(
                    "Copy code button text must be 25 characters or less"
                  );
                }

                // Additional validation for authentication templates
                if (templateData.authentication?.codeDelivery === "zero-tap") {
                  // Zero-tap specific validation
                  const apps = templateData.authentication?.apps || [];
                  apps.forEach((app, index) => {
                    if (app.packageName && !app.packageName.includes(".")) {
                      throw new Error(
                        `App ${
                          index + 1
                        }: Zero-tap requires fully qualified package names (e.g., com.example.app)`
                      );
                    }
                  });
                }
              }

              if (missingFields.length > 0) {
                message.error(
                  `Missing required fields: ${missingFields.join(", ")}`
                );
                return {
                  success: false,
                  error: `Missing required fields: ${missingFields.join(", ")}`,
                };
              }


              // Additional logging for authentication templates
              if (templateData.category === "Authentication") {
                // console.log(
                //   "Authentication template components:",
                //   JSON.stringify(components, null, 2)
                // );
                // console.log(
                //   "Authentication settings:",
                //   templateData.authentication
                // );
              }

              // Check if we should use the file handle API
              const useFileHandleApi = templateData.fileHandle && templateData.headerFormat;
              
              const response = await axiosConfig.post(
                useFileHandleApi ? "/template/create-with-file-handle" : "/template/create",
                useFileHandleApi ? {
                  ...requestBody,
                  fileHandle: templateData.fileHandle,
                  headerFormat: templateData.headerFormat,
                  s3Key: templateData.s3Key || templateData.components?.header?.s3Key || "",
                  mediaId: templateData.components.header.mediaId || ""
                } : requestBody
              );

              if (response.data && response.data.status) {
                if (response.data?.statuscode == "200") {
                  // Success - close the form and show success message
                  setShowAdvancedBuilder(false);
                  const msg = response.data?.message;
                  message.success(formatApiMessage(msg) || "Template created successfully");
                  await fetchTemplates(
                    activeFilters,
                    pagination.current,
                    pagination.pageSize
                  );
                  return { success: true };
                } else {
                  // Error response - don't close the form
                  const msgText = formatApiMessage(response.data?.message) || response.data?.statuscode || "Failed to create template";
                  message.error(msgText);
                  return { success: false, error: msgText };
                }
              } else {
                // Handle specific error messages from API - don't close the form
                const errorMessage = formatApiMessage(response.data?.message) || response.data?.statuscode || "Failed to create template";
                message.error(errorMessage);
                console.error("API Error Response:", response.data);
                return { success: false, error: errorMessage };
              }
            } catch (error) {
              console.error("Error creating template:", error);

              // Handle validation errors first with more specific error handling
              if (
                error.message &&
                (error.message.includes("Variables must be sequential") ||
                  error.message.includes("must be a valid HTTPS URL") ||
                  error.message.includes("Template name must contain only") ||
                  error.message.includes("Number of variables") ||
                  error.message.includes("should be numeric") ||
                  error.message.includes("should contain only letters") ||
                  error.message.includes("Button text is required") ||
                  error.message.includes("Examples are required") ||
                  error.message.includes("Example format is invalid") ||
                  error.message.includes("Package Name:") ||
                  error.message.includes("Signature Hash:") ||
                  error.message.includes("Zero-tap agreement") ||
                  error.message.includes("App") ||
                  error.message.includes("Code expiration time") ||
                  error.message.includes("button text must be"))
              ) {
                message.error(error.message);
                return { success: false, error: error.message };
              }

              // Handle different types of network/API errors
              let errorMessage = "Network error. Please try again.";
              if (error.response?.data) {
                const errorData = error.response.data;
                errorMessage = errorData.message || "Failed to create template";
                console.error("API Error Details:", errorData);
              }

              // Check if it's a Kafka connection error and handle it gracefully
              if (
                error.message &&
                error.message.includes("Kafka connection failed")
              ) {
                errorMessage =
                  "Template submitted but notification service is unavailable. Your template is being processed.";
                // Still return success for Kafka errors as the template was likely created
                return { success: true };
              }

              message.error(errorMessage);
              return { success: false, error: errorMessage };
            }
          }}
        />
      ) : (
        <>
          {/* Page Header */}
          <div className="template-page-header">
            <div>
              <h2 className="template-page-title">Templates</h2>
              <p className="template-page-subtitle">Create and manage your WhatsApp message templates</p>
            </div>
            <WriteGuard page="templates">
              <Space wrap>
                <Button
                  icon={<ThunderboltOutlined />}
                  onClick={() => setShowAdvancedBuilder(true)}
                  disabled={!loadingWabaDetails && !hasWabaDetails && !wabaFetchError}
                  size="large"
                  style={{
                    borderColor: (!loadingWabaDetails && !hasWabaDetails && !wabaFetchError) ? '#d9d9d9' : '#25D366',
                    color: (!loadingWabaDetails && !hasWabaDetails && !wabaFetchError) ? '#8c8c8c' : '#128C7E',
                    borderRadius: 10,
                    height: 42,
                    fontWeight: 600,
                    fontSize: 14,
                  }}
                >
                  Create with AI
                </Button>
                <Button
                  type="primary"
                  className="btn-create-template"
                  icon={<PlusOutlined />}
                  onClick={() => setShowAdvancedBuilder(true)}
                  disabled={!loadingWabaDetails && !hasWabaDetails && !wabaFetchError}
                  size="large"
                >
                  Create Template
                </Button>
              </Space>
            </WriteGuard>
          </div>

          <Card className="template-theme-card">
            <Tabs 
              activeKey={activeTab} 
              onChange={setActiveTab}
              size="large"
              className="templates-tabs"
              items={[
                {
                  key: 'mytemplates', 
                  label: (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <FileTextOutlined />
                      My Templates
                      <Badge count={pagination.total} style={{ backgroundColor: '#1890ff' }} />
                    </span>
                  ),
                  children: renderMyTemplates()
                },
                {
                  key: 'library',
                  label: (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <ThunderboltOutlined />
                      Template Library
                    </span>
                  ),
                  children: renderTemplateLibrary()
                },
              ]}
            />
          </Card>
        </>
      )}

      {/* Advanced Filter Modal */}
      <Modal
        title="Advanced Filter"
        open={advancedFilterVisible}
        onCancel={() => setAdvancedFilterVisible(false)}
        footer={null}
        width={500}
      >
        <Form
          form={filterForm}
          layout="vertical"
          onFinish={handleAdvancedFilterSubmit}
        >
          <Form.Item name="templatename" label="Template Name">
            <Input placeholder="Enter template name" />
          </Form.Item>

          <Form.Item name="status" label="Status">
            <Select placeholder="Select status" allowClear>
              <Option value="Pending">Pending</Option>
              <Option value="Approved">Approved</Option>
              <Option value="Rejected">Rejected</Option>
              <Option value="Discarded">Discarded</Option>
            </Select>
          </Form.Item>

          <Form.Item name="templatecategory" label="Category">
            <Select placeholder="Select category" allowClear>
              <Option value="MARKETING">Marketing</Option>
              <Option value="UTILITY">Utility</Option>
              <Option value="AUTHENTICATION">Authentication</Option>
            </Select>
          </Form.Item>

          {/* <Form.Item name="language" label="Language">
            <Select placeholder="Select language" allowClear>
              <Option value="en">English</Option>
              <Option value="hi">Hindi</Option>
              <Option value="mr">Marathi</Option>
              <Option value="gu">Gujarati</Option>
            </Select>
          </Form.Item> */}

          <Form.Item>
            <Space style={{ width: "100%", justifyContent: "flex-end" }}>
              <Button onClick={() => setAdvancedFilterVisible(false)}>
                Cancel
              </Button>
              <Button
                onClick={() => {
                  filterForm.resetFields();
                  handleClearFilters();
                }}
              >
                Clear
              </Button>
              <Button type="primary" htmlType="submit">
                Apply Filter
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Create Template Modal */}
      <Modal
        title="Create New Template"
        open={createTemplateVisible}
        onCancel={() => setCreateTemplateVisible(false)}
        footer={null}
        width={700}
      >
        <Form
          form={createForm}
          layout="vertical"
          onFinish={handleCreateTemplate}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="Template Name"
                rules={[
                  { required: true, message: "Please enter template name" },
                ]}
              >
                <Input placeholder="Enter template name" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                name="language"
                label="Language"
                rules={[{ required: true, message: "Please select language" }]}
                initialValue="en"
              >
                <Select placeholder="Select language" defaultValue="en">
                  <Option value="en">English</Option>
                  <Option value="hi">Hindi</Option>
                  <Option value="mr">Marathi</Option>
                  <Option value="gu">Gujarati</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                name="category"
                label="Category"
                rules={[{ required: true, message: "Please select category" }]}
              >
                <Select placeholder="Select category">
                  <Option value="Utility">Utility</Option>
                  <Option value="Marketing">Marketing</Option>
                  <Option value="Authentication">Authentication</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: true, message: "Please enter description" }]}
          >
            <Input placeholder="Enter template description" />
          </Form.Item>

          <Form.Item
            name="content"
            label="Message Content"
            rules={[
              { required: true, message: "Please enter message content" },
            ]}
          >
            <TextArea
              rows={6}
              placeholder="Enter your message. Use {{variable_name}} for dynamic content"
            />
          </Form.Item>

          <Alert
            message="Template Variables"
            description="Use {{variable_name}} syntax to add dynamic content. Common variables: {{name}}, {{order_id}}, {{date}}, {{amount}}"
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Form.Item>
            <Space style={{ width: "100%", justifyContent: "flex-end" }}>
              <Button onClick={() => setCreateTemplateVisible(false)}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                Create Template
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit Template Modal */}
      <Modal
        title="Edit Template"
        open={editTemplateVisible}
        onCancel={() => setEditTemplateVisible(false)}
        footer={null}
        width={700}
      >
        <Form form={editForm} layout="vertical" onFinish={handleUpdateTemplate}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="Template Name"
                rules={[
                  { required: true, message: "Please enter template name" },
                ]}
              >
                <Input placeholder="Enter template name" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                name="category"
                label="Category"
                rules={[{ required: true, message: "Please select category" }]}
              >
                <Select placeholder="Select category">
                  <Option value="Utility">Utility</Option>
                  <Option value="Marketing">Marketing</Option>
                  <Option value="Authentication">Authentication</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="status" label="Status">
                <Select>
                  <Option value="active">Active</Option>
                  <Option value="inactive">Inactive</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: true, message: "Please enter description" }]}
          >
            <Input placeholder="Enter template description" />
          </Form.Item>

          <Form.Item
            name="content"
            label="Message Content"
            rules={[
              { required: true, message: "Please enter message content" },
            ]}
          >
            <TextArea
              rows={6}
              placeholder="Enter your message. Use {{variable_name}} for dynamic content"
            />
          </Form.Item>

          <Form.Item>
            <Space style={{ width: "100%", justifyContent: "flex-end" }}>
              <Button onClick={() => setEditTemplateVisible(false)}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                Update Template
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Preview Template Modal */}
      <Modal
        title="Template Preview"
        open={previewTemplateVisible}
        onCancel={() => setPreviewTemplateVisible(false)}
        footer={
          <Button
            type="primary"
            onClick={() => setPreviewTemplateVisible(false)}
          >
            Close
          </Button>
        }
        width={600}
      >
        {selectedTemplate && (
          <div>
            <div style={{ marginBottom: 24 }}>
              <Title level={4} style={{ marginBottom: 8 }}>
                {selectedTemplate.name}
              </Title>

              {/* Template Info */}
              <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col span={8}>
                  <div style={{ fontSize: 12, color: "#666", marginBottom: 4 }}>
                    Category
                  </div>
                  <Tag color="purple">
                    {selectedTemplate.category?.toUpperCase() || "UTILITY"}
                  </Tag>
                </Col>
                <Col span={8}>
                  <div style={{ fontSize: 12, color: "#666", marginBottom: 4 }}>
                    Language
                  </div>
                  <Tag color="blue">
                    {selectedTemplate.language?.toUpperCase() || "EN"}
                  </Tag>
                </Col>
                <Col span={8}>
                  <div style={{ fontSize: 12, color: "#666", marginBottom: 4 }}>
                    Status
                  </div>
                  <Tag
                    color={
                      selectedTemplate.status === "active" ? "green" : "orange"
                    }
                    icon={
                      selectedTemplate.status === "active" ? (
                        <CheckCircleOutlined />
                      ) : (
                        <ClockCircleOutlined />
                      )
                    }
                  >
                    {selectedTemplate.status === "active"
                      ? "APPROVED"
                      : selectedTemplate.originalStatus?.toUpperCase() ||
                        "PENDING"}
                  </Tag>
                </Col>
              </Row>
            </div>

            {/* Message Content */}
            <Card title="Message Content" style={{ marginBottom: 16 }}>
              <div
                style={{
                  background: "#f5f5f5",
                  padding: 16,
                  borderRadius: 8,
                  fontFamily: "system-ui, -apple-system, sans-serif",
                  whiteSpace: "pre-wrap",
                  lineHeight: 1.5,
                  fontSize: 14,
                  minHeight: 100,
                  border: "1px solid #e8e8e8",
                }}
              >
                {selectedTemplate.body || selectedTemplate.content}
              </div>
            </Card>

            {/* Footer */}
            {selectedTemplate.footer && (
              <Card title="Footer" style={{ marginBottom: 16 }}>
                <div
                  style={{
                    background: "#f5f5f5",
                    padding: 12,
                    borderRadius: 8,
                    color: "#8696a0",
                    fontSize: 13,
                    border: "1px solid #e8e8e8",
                  }}
                >
                  {selectedTemplate.footer}
                </div>
              </Card>
            )}

            {/* Buttons */}
            {selectedTemplate.buttons && selectedTemplate.buttons.length > 0 && (
              <Card title="Template Buttons" style={{ marginBottom: 16 }}>
                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                  {selectedTemplate.buttons.map((button, index) => (
                    <div
                      key={index}
                      style={{
                        border: "1px solid #e8e8e8",
                        borderRadius: "4px",
                        padding: "12px",
                        backgroundColor: "#fafafa",
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", marginBottom: "8px" }}>
                        <div
                          style={{
                            width: "24px",
                            height: "24px",
                            borderRadius: "4px",
                            background: "#e6f7ff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#1890ff",
                            marginRight: "8px",
                            flexShrink: 0,
                          }}
                        >
                          {button.type === "URL" && <LinkOutlined />}
                          {button.type === "PHONE_NUMBER" && <PhoneOutlined />}
                          {button.type === "QUICK_REPLY" && <MessageOutlined />}
                        </div>
                        <Text strong style={{ fontSize: "14px" }}>
                          {button.text}
                        </Text>
                        <Tag color="blue" style={{ marginLeft: "8px" }}>
                          {button.type.replace("_", " ")}
                        </Tag>
                      </div>

                      {button.url && (
                        <div style={{ marginTop: "4px" }}>
                          <Text type="secondary" style={{ fontSize: "12px" }}>
                            URL:
                          </Text>
                          <Text style={{ fontSize: "12px", wordBreak: "break-word" }}>
                            {button.url}
                          </Text>
                        </div>
                      )}

                      {button.phone_number && (
                        <div style={{ marginTop: "4px" }}>
                          <Text type="secondary" style={{ fontSize: "12px" }}>
                            Phone:
                          </Text>
                          <Text style={{ fontSize: "12px" }}>
                            {button.phone_number}
                          </Text>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Variables Used */}
            <Card title="Variables Used" size="small">
              <Space wrap>
                {(selectedTemplate.variables || []).map((variable) => (
                  <Tag color="blue" key={variable}>
                    {`{{${variable}}}`}
                  </Tag>
                ))}
              </Space>
              {(!selectedTemplate.variables || selectedTemplate.variables.length === 0) && (
                <Text type="secondary">No variables used in this template</Text>
              )}
            </Card>
          </div>
        )}
      </Modal>

      {/* Mobile WhatsApp Preview Modal */}
      <Modal
        title="WhatsApp Preview"
        open={mobilePreviewVisible}
        onCancel={() => setMobilePreviewVisible(false)}
        footer={
          <Button type="primary" onClick={() => setMobilePreviewVisible(false)}>
            Close
          </Button>
        }
        width={500}
      >
        {selectedTemplate && (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <div className="whatsapp-preview-container">
              {/* Chat Header */}
              <div className="chat-header">
                <div className="chat-avatar whatsapp-avatar">
                  <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="20" cy="20" r="20" fill="#25D366"/>
                    <path d="M20 10C17.79 10 16 11.79 16 14C16 16.21 17.79 18 20 18C22.21 18 24 16.21 24 14C24 11.79 22.21 10 20 10ZM20 20C16.67 20 10 21.67 10 25V28H30V25C30 21.67 23.33 20 20 20Z" fill="white"/>
                  </svg>
                </div>
                <div className="chat-info">
                  <h3 className="chat-name">{(() => {
                    // Resolve company name from session storage (fallback to WhatsApp Business)
                    let companyName = 'WhatsApp Business';
                    try {
                      const usersStr = sessionStorage.getItem('user');
                      if (usersStr) {
                        const usersObj = JSON.parse(usersStr);
                        if (usersObj && usersObj.name) {
                          companyName = usersObj.name;
                        }
                      }
                    } catch (err) {
                      // keep default
                    }
                    return companyName;
                  })()}</h3>
                  <p className="chat-status">Template Preview</p>
                </div>
                <span className="template-category">{selectedTemplate.category?.toUpperCase() || 'UTILITY'}</span>
              </div>

              {/* Chat Area */}
              <div className="chat-area">
                <div className="message-container">
                  <div className="message-bubble">
                    {/* Header Content */}
                    {(() => {
                      const headerComponent = selectedTemplate.component?.find(c => c.type === 'HEADER');
                      const mediaUrl = selectedTemplate.header_media_url;
                      const format = selectedTemplate.header_format || headerComponent?.format;
                      
                      if (mediaUrl && format === 'IMAGE') {
                        return (
                          <div className="message-header-content">
                            <img src={mediaUrl} alt="Template header" style={{ width: '100%', borderRadius: '8px 8px 0 0', maxHeight: 200, objectFit: 'cover', display: 'block' }} />
                          </div>
                        );
                      }
                      if (mediaUrl && format === 'VIDEO') {
                        return (
                          <div className="message-header-content">
                            <video src={mediaUrl} style={{ width: '100%', borderRadius: '8px 8px 0 0', maxHeight: 200, objectFit: 'cover', display: 'block' }} controls preload="metadata" />
                          </div>
                        );
                      }
                      if (mediaUrl && format === 'DOCUMENT') {
                        return (
                          <div className="message-header-content" style={{ padding: '12px', background: '#f0f4f8', borderRadius: '8px 8px 0 0', display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontSize: 28 }}>📄</span>
                            <a href={mediaUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#1890ff', wordBreak: 'break-all' }}>View Document</a>
                          </div>
                        );
                      }
                      if (headerComponent?.format === 'TEXT') {
                        return (
                          <div className="message-header-text">
                            {headerComponent.text || 'Header text'}
                          </div>
                        );
                      }

                      if (headerComponent) {
                        return (
                          <div className="message-header-content">
                            {headerComponent.format === 'IMAGE' && (
                              <div className="header-image">
                                📷 Image Preview
                              </div>
                            )}
                            {headerComponent.format === 'VIDEO' && (
                              <div className="header-image">
                                🎥 Video Preview
                              </div>
                            )}
                            {headerComponent.format === 'DOCUMENT' && (
                              <div className="header-image">
                                📄 Document Preview
                              </div>
                            )}
                          </div>
                        );
                      }
                      return null;
                    })()}

                    {/* Message Body */}
                    <div className="message-content">
                      {(() => {
                        // Prefer pre-parsed body (footer already stripped)
                        if (selectedTemplate.body) return selectedTemplate.body;
                        const body = selectedTemplate.component?.find(c => c.type === 'BODY');
                        return body?.text || selectedTemplate.content || '';
                      })()}
                    </div>

                    {/* Footer */}
                    {(() => {
                      // Prefer pre-parsed footer, fall back to FOOTER component
                      const footerText = selectedTemplate.footer
                        || selectedTemplate.component?.find(c => c.type === 'FOOTER')?.text
                        || null;
                      if (footerText) {
                        return (
                          <div className="message-footer-text" style={{
                            fontSize: '12px',
                            color: '#8696a0',
                            marginTop: 4,
                            marginBottom: 2,
                            lineHeight: 1.4
                          }}>
                            {footerText}
                          </div>
                        );
                      }
                      return null;
                    })()}

                    {/* Buttons */}
                    {(() => {
                      const buttonsComp = selectedTemplate.component?.find(c => c.type === 'BUTTONS');
                      if (buttonsComp && buttonsComp.buttons && Array.isArray(buttonsComp.buttons) && buttonsComp.buttons.length > 0) {
                        return (
                          <div className="template-buttons">
                            {buttonsComp.buttons.map((button, index) => (
                              <button key={index} type="button" className="template-button">
                                {button.type === 'URL' && '🔗'}
                                {button.type === 'PHONE_NUMBER' && '📞'}
                                {button.type === 'QUICK_REPLY' && '💬'}
                                {button.text || `Button ${index + 1}`}
                              </button>
                            ))}
                          </div>
                        );
                      }
                      return null;
                    })()}

                    {/* Message Footer with Time and Checkmarks */}
                    <div className="message-footer">
                      <div className="message-time">
                        {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        <svg width="16" height="15" viewBox="0 0 16 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M15.01 3.316l-.478-.372a.365.365 0 0 0-.51.063L8.666 9.879a.32.32 0 0 1-.484.033l-.358-.325a.319.319 0 0 0-.484.033l-.378.387a.32.32 0 0 0 .033.484l1.025.93a.32.32 0 0 0 .484-.033l6.272-8.048a.366.366 0 0 0-.063-.51zm-4.1 0l-.478-.372a.365.365 0 0 0-.51.063L4.566 9.879a.32.32 0 0 1-.484.033L3.724 9.587a.319.319 0 0 0-.484.033l-.378.387a.32.32 0 0 0 .033.484l1.025.93a.32.32 0 0 0 .484-.033L10.676 3.84a.366.366 0 0 0-.063-.51z" fill="#4fc3f7"/>
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Template Preview Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <EyeOutlined />
            Template Preview
            {previewTemplate && (
              <Tag color="purple">{previewTemplate.category}</Tag>
            )}
          </div>
        }
        open={templatePreviewVisible}
        onCancel={() => {
          setTemplatePreviewVisible(false);
          setPreviewTemplate(null);
        }}
        footer={[
          <Button 
            key="copy" 
            icon={<CopyOutlined />}
            onClick={() => handleCopyTemplateId(previewTemplate?.id)}
          >
            Copy Template ID
          </Button>,
          <Button 
            key="close" 
            onClick={() => {
              setTemplatePreviewVisible(false);
              setPreviewTemplate(null);
            }}
          >
            Close
          </Button>
        ]}
        width={800}
      >
        {previewTemplate && (
          <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
            {/* Template Info */}
            <div style={{ marginBottom: 16 }}>
              <Title level={4}>{previewTemplate.name}</Title>
              <Space wrap>
                <Tag color="purple">{previewTemplate.category}</Tag>
                <Tag color="blue">{previewTemplate.topic}</Tag>
                {previewTemplate.industry && (
                  <Tag color="green">{previewTemplate.industry}</Tag>
                )}
              </Space>
            </div>

            {/* Use Case */}
            {previewTemplate.useCase && (
              <div style={{ marginBottom: 16 }}>
                <Text strong>Use Case: </Text>
                <Text>{previewTemplate.useCase}</Text>
              </div>
            )}

            {/* Template Content */}
            <div style={{ 
              background: '#f5f5f5', 
              padding: 16, 
              borderRadius: 8,
              marginBottom: 16 
            }}>
              {(() => {
                const headerComponent = previewTemplate.component?.find(c => c.type === 'HEADER');
                const mediaUrl = previewTemplate.header_media_url;
                const format = previewTemplate.header_format || headerComponent?.format;
                const headerText = previewTemplate.header || headerComponent?.text;
                
                if (mediaUrl && format === 'IMAGE') {
                  return (
                    <div style={{ marginBottom: 12 }}>
                      <Text strong>Header:</Text>
                      <div style={{ background: 'white', padding: 0, borderRadius: 4, marginTop: 4, overflow: 'hidden' }}>
                        <img src={mediaUrl} alt="Template header" style={{ width: '100%', maxHeight: 240, objectFit: 'cover', display: 'block', borderRadius: 4 }} />
                      </div>
                    </div>
                  );
                }
                if (mediaUrl && format === 'VIDEO') {
                  return (
                    <div style={{ marginBottom: 12 }}>
                      <Text strong>Header:</Text>
                      <div style={{ background: 'white', padding: 0, borderRadius: 4, marginTop: 4, overflow: 'hidden' }}>
                        <video src={mediaUrl} style={{ width: '100%', maxHeight: 240, objectFit: 'cover', display: 'block', borderRadius: 4 }} controls preload="metadata" />
                      </div>
                    </div>
                  );
                }
                if (mediaUrl && format === 'DOCUMENT') {
                  return (
                    <div style={{ marginBottom: 12 }}>
                      <Text strong>Header:</Text>
                      <div style={{ background: 'white', padding: '8px 12px', borderRadius: 4, marginTop: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 24 }}>📄</span>
                        <a href={mediaUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#1890ff', wordBreak: 'break-all' }}>View Document</a>
                      </div>
                    </div>
                  );
                }
                if (headerText) {
                  return (
                    <div style={{ marginBottom: 12 }}>
                      <Text strong>Header:</Text>
                      <div style={{ background: 'white', padding: 8, borderRadius: 4, marginTop: 4 }}>
                        {headerText}
                      </div>
                    </div>
                  );
                }
                if (format === 'IMAGE') {
                  return (
                    <div style={{ marginBottom: 12 }}>
                      <Text strong>Header:</Text>
                      <div style={{ background: 'white', padding: 8, borderRadius: 4, marginTop: 4, color: '#999' }}>📷 Image Header</div>
                    </div>
                  );
                }
                if (format === 'VIDEO') {
                  return (
                    <div style={{ marginBottom: 12 }}>
                      <Text strong>Header:</Text>
                      <div style={{ background: 'white', padding: 8, borderRadius: 4, marginTop: 4, color: '#999' }}>🎥 Video Header</div>
                    </div>
                  );
                }
                if (format === 'DOCUMENT') {
                  return (
                    <div style={{ marginBottom: 12 }}>
                      <Text strong>Header:</Text>
                      <div style={{ background: 'white', padding: 8, borderRadius: 4, marginTop: 4, color: '#999' }}>📄 Document Header</div>
                    </div>
                  );
                }
                return null;
              })()}

              <div style={{ marginBottom: 12 }}>
                <Text strong>Body:</Text>
                <div style={{ 
                  background: 'white', 
                  padding: 8, 
                  borderRadius: 4, 
                  marginTop: 4,
                  whiteSpace: 'pre-wrap'
                }}>
                  {previewTemplate.body || previewTemplate.content || 'No content available'}
                </div>
              </div>

              {previewTemplate.footer && (
                <div>
                  <Text strong>Footer:</Text>
                  <div style={{ 
                    background: 'white', 
                    padding: 8, 
                    borderRadius: 4, 
                    marginTop: 4,
                    color: '#8696a0',
                    fontSize: 13
                  }}>
                    {previewTemplate.footer}
                  </div>
                </div>
              )}
            </div>

            {/* Buttons */}
            {previewTemplate.buttons && previewTemplate.buttons.length > 0 && (
              <div>
                <Text strong>Buttons:</Text>
                <div style={{ marginTop: 8 }}>
                  {previewTemplate.buttons.map((button, index) => (
                    <Tag 
                      key={index} 
                      color={
                        button.type === 'URL' ? 'blue' : 
                        button.type === 'PHONE_NUMBER' ? 'green' : 
                        'default'
                      }
                      style={{ margin: '2px 4px 2px 0' }}
                    >
                      {button.type === 'URL' && <LinkOutlined />}
                      {button.type === 'PHONE_NUMBER' && <PhoneOutlined />}
                      {button.type === 'QUICK_REPLY' && <MessageOutlined />}
                      {' '}{button.text}
                    </Tag>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Adopt Template from Library Modal */}
      <Modal
        title="Adopt Template"
        open={useLibraryTemplateVisible}
        onCancel={handleUseLibraryTemplateCancel}
        footer={[
          <Button key="cancel" onClick={handleUseLibraryTemplateCancel}>
            Cancel
          </Button>,
          <Button
            key="adopt"
            type="primary"
            loading={useLibraryTemplateLoading}
            onClick={handleAdoptTemplate}
            style={{ background: '#0036b0', borderColor: '#0036b0' }}
          >
            Adopt Template
          </Button>,
        ]}
        width={580}
      >
        {selectedLibraryTemplate && (
          <div>
            {/* Template Preview */}
            {(() => {
              // Library templates: data is in template_data; fallback to components[]
              const tplData = selectedLibraryTemplate.template_data;
              const bodyText = tplData?.body
                ? tplData.body.replace(/\{\{(\d+)\}\}/g, (_, n) => (tplData.body_params || [])[n - 1] || `{{${n}}}`)
                : (() => {
                    const comps = selectedLibraryTemplate.components || selectedLibraryTemplate.component || [];
                    const bodyComp = comps.find(c => c.type === 'BODY');
                    return bodyComp?.text || selectedLibraryTemplate.content || selectedLibraryTemplate.body || '';
                  })();
              const headerText = tplData?.header || null;
              const buttons = tplData?.buttons?.length > 0
                ? tplData.buttons
                : (() => {
                    const comps = selectedLibraryTemplate.components || selectedLibraryTemplate.component || [];
                    const buttonsComp = comps.find(c => c.type === 'BUTTONS');
                    return buttonsComp?.buttons || [];
                  })();
              if (!bodyText && !buttons.length) return null;
              return (
                <div style={{ marginBottom: 20 }}>
                  <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 6 }}>
                    Template Preview
                  </Text>
                  <div style={{ background: '#e5ddd5', borderRadius: 8, padding: 12 }}>
                    <div style={{
                      background: '#dcf8c6',
                      borderRadius: 8,
                      padding: '10px 12px',
                      fontSize: 13,
                      lineHeight: '1.5',
                      maxWidth: '80%',
                      marginLeft: 'auto',
                    }}>
                      {headerText && (
                        <div style={{ fontWeight: 600, marginBottom: 6 }}>{headerText}</div>
                      )}
                      {bodyText ? (
                        <div style={{ whiteSpace: 'pre-wrap' }}>{bodyText}</div>
                      ) : null}
                      {buttons.length > 0 && (
                        <div style={{
                          marginTop: 8,
                          borderTop: '1px solid rgba(0,0,0,0.1)',
                          paddingTop: 8,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 4,
                        }}>
                          {buttons.map((btn, idx) => {
                            const label = btn.text
                              || (btn.otp_type === 'COPY_CODE' ? 'Copy Code' : null)
                              || btn.type;
                            return (
                              <div key={idx} style={{
                                textAlign: 'center',
                                color: '#0b5cad',
                                fontSize: 12,
                                fontWeight: 500,
                                padding: '2px 0',
                              }}>
                                {btn.type === 'URL' && '🔗 '}{btn.type === 'PHONE_NUMBER' && '📞 '}
                                {label}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })()}

            <Form form={adoptForm} layout="vertical">
              <Form.Item
                name="customName"
                label="Template Name"
                rules={[
                  { required: true, message: 'Template name is required' },
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();
                      if (!/^[a-z0-9_]{1,512}$/.test(value)) {
                        return Promise.reject(new Error('Name must be lowercase letters, digits, or underscores only'));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
                extra="Only lowercase letters, digits, and underscores (max 512 chars)"
              >
                <Input
                  placeholder="e.g. my_custom_template"
                  maxLength={512}
                />
              </Form.Item>

              <Form.Item
                name="language"
                label="Language"
                initialValue="en_US"
                rules={[{ required: true, message: 'Please select a language' }]}
              >
                <Select>
                  <Option value="en_US">English (US)</Option>
                  <Option value="en_GB">English (GB)</Option>
                  <Option value="hi">Hindi</Option>
                  <Option value="mr">Marathi</Option>
                  <Option value="gu">Gujarati</Option>
                  <Option value="ta">Tamil</Option>
                  <Option value="te">Telugu</Option>
                  <Option value="kn">Kannada</Option>
                  <Option value="ml">Malayalam</Option>
                  <Option value="bn">Bengali</Option>
                  <Option value="pa">Punjabi</Option>
                  <Option value="ur">Urdu</Option>
                </Select>
              </Form.Item>
            </Form>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default TemplatesPage;
